import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";

import GridLayout from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { useSelector, useStore } from 'react-redux';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  CircularProgress, IconButton, Button, Avatar, Box,
  Stack, Typography, Select, MenuItem, FormControl,
  TextField, Chip, Paper, Divider, FormControlLabel, Checkbox,
  Switch, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Slider
} from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/DeleteOutline';

// Business Components imports
import { ZmanimWidget } from "../../../business/ZmanimWidget/ZmanimWidget";
import { WeatherWidget } from "../../../business/WeatherWidget/WeatherWidget";
import { ShabbatWidget } from "../../../business/ShabbatWidget/ShabbatWidget";
import { TextWidget } from "../../../business/TextWidget/TextWidget";
import { ImageWidget } from "../../../business/ImageWidget/ImageWidget";
import { NoticeBoardWidget } from "../../../business/NoticeBoardWidget/NoticeBoardWidget";
import { EmergencyWidget } from "../../../business/EmergencyWidget/EmergencyWidget";
import { ContactsWidget } from "../../../business/ContactsWidget/ContactsWidget";
import { SimchaWidget } from "../../../business/SimchaWidget/SimchaWidget";
import { NitzavSpotlightWidget } from "../../../business/NitzavSpotlightWidget/NitzavSpotlightWidget";
import { MoadWidget } from "../../../business/MoadWidget/MoadWidget";
import { ParnasWidget } from "../../../business/ParnasWidget/ParnasWidget";
import { OmerWidget } from "../../../business/OmerWidget/OmerWidget";
import { HalachicTimesWidget } from "../../../business/HalachicTimesWidget/HalachicTimesWidget";
import { InyaneiHaYomWidget } from "../../../business/InyaneiHaYomWidget/InyaneiHaYomWidget";
import { LimudimWidget } from "../../../business/LimudimWidget/LimudimWidget";
import { WIDGET_ICON_MAP } from "../../../business/widgetIconMap";
import { TefilotWidget } from "../../../business/TefilotWidget/TefilotWidget";
import { ExpensesAnalyticsWidget } from "../../../business/ExpensesAnalyticsWidget/ExpensesAnalyticsWidget";
import { FinancialGaugeWidget } from "../../../business/FinancialGaugeWidget/FinancialGaugeWidget";
import { ClockWidget } from "../../../business/ClockWidget/ClockWidget";
import { NextMinyaWidget } from "../../../business/NextMinyaWidget/NextMinyaWidget";
import { SeatingMapWidget } from "../../../business/SeatingMapWidget/SeatingMapWidget";
import { DocumentWidget } from "../../../business/DocumentWidget/DocumentWidget";
import AutoScaler from "../../../business/AutoScaler/AutoScaler";
import WidgetWrapper from "../../../business/WidgetWrapper/WidgetWrapper";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { heritageTheme, heritageThemeOptions } from '../../../heritageTheme.ts';
import { ConfigDialog } from '../../../dummies/ConfigDialog/ConfigDialog';

import { CenterDialog } from '../../../dummies/CenterDialog/CenterDialog';
import WidgetConfigurator from '../../../dummies/WidgetConfigurator/WidgetConfigurator';

import ZmanSelect from '../../../dummies/WidgetSetupWizard/components/ZmanSelect';

import { addWidget, removeWidget, updateWidget, updateDashboardLocal, updateDashboardSettings, uploadImage, deleteImage, copyWidgetFromDashboard, moveWidgetFromSaved } from './actions/dashboardActions';
import { selectLicense, selectPlanDefinitions } from '../../../store/actions/licenseActions';
import { isWidgetAllowed, getEffectiveLimit, isUnlimited } from '../../../utils/licenseUtils';
import LockedWidgetPlaceholder from '../../../dummies/LockedWidgetPlaceholder/LockedWidgetPlaceholder';

const findFirstFreeSpace = (layout, cols, maxRows, w, h, startFromBottom = false) => {

  // בדיקה ראשונית: האם הווידג'ט גדול מדי?
  if (w > cols || h > maxRows) {
    console.log('❌ Widget too large:', { w, h, cols, maxRows });
    return null;
  }

  let matrix = Array(maxRows).fill(null).map(() => Array(cols).fill(false));

  // סמן את כל התאים התפוסים והדפס את הווידג'טים
  console.log('📦 Current widgets:');
  layout.forEach((item, index) => {
    console.log(`  Widget ${index + 1}:`, {
      componentKey: item.componentKey,
      position: { x: item.dashboardLayout.x, y: item.dashboardLayout.y },
      size: { w: item.dashboardLayout.w, h: item.dashboardLayout.h },
      endsAt: {
        x: item.dashboardLayout.x + item.dashboardLayout.w,
        y: item.dashboardLayout.y + item.dashboardLayout.h
      }
    });

    for (let i = 0; i < item.dashboardLayout.h; i++) {
      for (let j = 0; j < item.dashboardLayout.w; j++) {
        const y = item.dashboardLayout.y + i;
        const x = item.dashboardLayout.x + j;
        if (y < maxRows && x < cols) {
          matrix[y][x] = true;
        }
      }
    }
  });

  // קביעת כיוון החיפוש
  const yStart = startFromBottom ? maxRows - h : 0;
  const yEnd = startFromBottom ? -1 : maxRows - h + 1;
  const yStep = startFromBottom ? -1 : 1;


  // חיפוש מקום פנוי
  for (let y = yStart; startFromBottom ? y >= yEnd : y < yEnd; y += yStep) {
    for (let x = 0; x <= cols - w; x++) {
      let isFree = true;
      for (let i = 0; i < h; i++) {
        for (let j = 0; j < w; j++) {
          if (matrix[y + i][x + j]) {
            isFree = false;
            break;
          }
        }
        if (!isFree) break;
      }
      if (isFree) {
        console.log('✅ Found free space:', { x, y, w, h, willEndAt: { x: x + w, y: y + h } });
        return { x, y };
      }
    }
  }

  console.log('❌ No free space found - dashboard is full');
  return null;
};

const Dashboard = ({ onLogout, dispatch, hasUnsavedChanges, isSaving, style, isEditMode: isEditModeProp, dashboard: dashboardProp, organization: organizationProp, catalog: catalogProp, onHandlersReady, isDemo = false }) => {

  // Redux store access
  const store = useStore();

  // רזולוציית המטרה - לפי זה אנחנו בונים את העיצוב
  const TARGET_WIDTH = 1920;
  const TARGET_HEIGHT = 1080;

  // גודל פונט בסיס לווידג'טים ללא AutoScaler
  // גודל בפיקסלים עבור רזולוציית 1920x1080
  const BASE_FONT_SIZE = 30;

  // חישוב הסקייל הדרוש - כמה צריך להקטין/להגדיל כדי להיכנס למסך הנוכחי
  const [scale, setScale] = useState(1);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const container = containerRef.current;
      const width = container ? container.clientWidth : window.innerWidth;
      const height = container ? container.clientHeight : window.innerHeight;

      // מחשבים את היחס בין הרוחב האמיתי לרוחב הרצוי
      const scaleX = width / TARGET_WIDTH;
      const scaleY = height / TARGET_HEIGHT;
      // לוקחים את הקטן מביניהם כדי שהכל ייכנס למסך בלי חיתוך (Contain)
      // או משתמשים ב-scaleX אם רוצים למלא רוחב תמיד
      const newScale = Math.min(scaleX, scaleY) * (isEditModeProp ? 0.98 : 1);
      setScale(newScale > 0 ? newScale : 1);
    };

    const observer = new ResizeObserver(handleResize);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    handleResize();
    // fallback ל-WV91 שבו ResizeObserver לא תמיד מופעל על טעינה ראשונה
    const raf = requestAnimationFrame(handleResize);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, []);

  // Redux selectors - use specific selectors to ensure re-renders
  // אם קיבלנו dashboard כ-prop (מצב ציבורי), השתמש בו. אחרת, קח מ-Redux
  const screenFromRedux = useSelector(state => state.dashboard.dashboard);
  const screen = dashboardProp || screenFromRedux;

  // hasUnsavedChanges from props
  // isSaving from props
  const catalogFromRedux = useSelector(state => state.app?.catalog || []);
  const catalog = catalogProp || catalogFromRedux;
  const { data: zmanim, loading: zmanimLoading, error: zmanimError, shabbat: zmanimShabbat, hebrewDate } = useSelector(state => state.zmanim || {});
  const { data: weather, loading: weatherLoading, error: weatherError } = useSelector(state => state.weather || {});
  const { data: shabbat, loading: shabbatLoading, error: shabbatError } = useSelector(state => state.shabbat || {});
  const moadByWidgetId    = useSelector(state => state.moad?.byWidgetId    || {});
  const tefilotByWidgetId = useSelector(state => state.tefilot?.byWidgetId || {});
  const parnassByWidgetId = useSelector(state => state.parnas?.byWidgetId  || {});
  const omerByWidgetId    = useSelector(state => state.omer?.byWidgetId    || {});
  const engineZmanim      = useSelector(state => state.halachicEngine?.today?.zmanim || null);
  const isHebrewLeapYear = hebrewDate?.hy ? (7 * hebrewDate.hy + 1) % 19 < 7 : false;
  const organizationFromRedux = useSelector(state => state.organization);
  const organization = organizationProp || organizationFromRedux;
  const reduxLicense = useSelector(selectLicense);
  const reduxPlanDefs = useSelector(selectPlanDefinitions);

  // Router hooks
  const { dashboardId } = useParams();

  // Extract data from screen object
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const widgets = screen?.current?.widgets || [];

  const screenId = screen?.screenId;

  // Check if we're viewing a specific dashboard (read-only mode)
  // אם קיבלנו isEditModeProp, השתמש בו. אחרת, השתמש בלוגיקה הישנה
  const isViewingVersion = isEditModeProp !== undefined ? !isEditModeProp : !!dashboardId;

  // Local state
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);

  // ── Unified Widget Configurator ──
  const [configuratorOpen, setConfiguratorOpen] = useState(false);
  const [configuratorKey, setConfiguratorKey] = useState(null);
  const [configuratorInitialData, setConfiguratorInitialData] = useState(null);

  // Dashboard settings (read-only for display)
  const [dashboardBgColor, setDashboardBgColor] = useState('#fcfbfa');
  const [dashboardBgImage, setDashboardBgImage] = useState('');
  const [dashboardBorderRadius, setDashboardBorderRadius] = useState(10);
  const [dashboardFontFamily, setDashboardFontFamily] = useState('default');

  // ── תבנית MUI דינמית לפי פונט הלוח ──────────────────────────────────────
  const FONT_MAP = {
    default:        'inherit',
    rubik:          "'Rubik', sans-serif",
    assistant:      "'Assistant', sans-serif",
    heebo:          "'Heebo', sans-serif",
    frankRuhlLibre: "'Frank Ruhl Libre', serif",
    secularOne:     "'Secular One', sans-serif",
    alef:           "'Alef', sans-serif",
    suezOne:        "'Suez One', serif",
    notoSansHebrew: "'Noto Sans Hebrew', sans-serif",
    notoSerifHebrew:"'Noto Serif Hebrew', serif",
    miriamLibre:    "'Miriam Libre', monospace",
    syne:           "'Syne', sans-serif",
  };
  const widgetTheme = useMemo(() => {
    const fontFamily = FONT_MAP[dashboardFontFamily] || 'inherit';
    if (!fontFamily || fontFamily === 'inherit') return heritageTheme;
    return createTheme({
      ...heritageThemeOptions,
      typography: {
        ...heritageThemeOptions.typography,
        fontFamily,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardFontFamily]);

  const [dashboardWidgetShadow, setDashboardWidgetShadow] = useState('none');
  const [dashboardWidgetShadowColor, setDashboardWidgetShadowColor] = useState('#000000');
  const [tempBgColor, setTempBgColor] = useState('#fcfbfa');
  const [tempBgImage, setTempBgImage] = useState('');
  const [tempBorderRadius, setTempBorderRadius] = useState(35);
  const [tempFontFamily, setTempFontFamily] = useState('default');
  const [tempWidgetShadow, setTempWidgetShadow] = useState('none');
  const [tempWidgetShadowColor, setTempWidgetShadowColor] = useState('#000000');

  // Load settings from screen
  useEffect(() => {
    if (screen?.current?.settings) {
      setDashboardBgColor(screen.current.settings.bgColor || '#fcfbfa');
      setDashboardBgImage(screen.current.settings.bgImage || '');
      setDashboardBorderRadius(screen.current.settings.borderRadius ?? 10);
      setDashboardFontFamily(screen.current.settings.fontFamily || 'default');
      setDashboardWidgetShadow(screen.current.settings.widgetShadow || 'none');
      setDashboardWidgetShadowColor(screen.current.settings.widgetShadowColor || '#000000');
      setTempBgColor(screen.current.settings.bgColor || '#fcfbfa');
      setTempBgImage(screen.current.settings.bgImage || '');
      setTempBorderRadius(screen.current.settings.borderRadius ?? 10);
      setTempFontFamily(screen.current.settings.fontFamily || 'default');
      setTempWidgetShadow(screen.current.settings.widgetShadow || 'none');
      setTempWidgetShadowColor(screen.current.settings.widgetShadowColor || '#000000');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen?.current?.settings]);

  const [maxRows, setMaxRows] = useState(60);
  const [currentWidgetConfig, setCurrentWidgetConfig] = useState(null);
  const [editingWidget, setEditingWidget] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const [uploadingImage, setUploadingImage] = useState(false);
  const [hoveredWidget, setHoveredWidget] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [widgetToDelete, setWidgetToDelete] = useState(null);
  const [noSpaceDialogOpen, setNoSpaceDialogOpen] = useState(false);

  const COLS = 24; // הקטנת מספר העמודות מ-36 ל-24
  const ROW_HEIGHT = Math.floor(TARGET_HEIGHT * 0.021);
  const MARGIN = Math.floor(Math.min(TARGET_WIDTH, TARGET_HEIGHT) * 0.015);
  useEffect(() => {
    const availableHeight = TARGET_HEIGHT - MARGIN;
    const itemHeightSpace = ROW_HEIGHT + MARGIN;
    const calculatedMaxRows = Math.floor(availableHeight / itemHeightSpace);

    setMaxRows(calculatedMaxRows);
  }, [ROW_HEIGHT, MARGIN]);
  // Clear any old localStorage data on mount - Firestore is the single source of truth
  useEffect(() => {
    if (screenId) {
      const oldData = localStorage.getItem(`dashboard_${screenId}`);
      if (oldData) {
        console.log('Clearing old localStorage data for screen:', screenId);
        localStorage.removeItem(`dashboard_${screenId}`);
      }
    }
  }, [screenId]);

  const handleEditWidget = (widget) => {
    // מניעת עריכת ווידג'ט במצב צפייה ציבורית
    if (isViewingVersion) {
      console.warn('Cannot edit widget in public view mode');
      return;
    }

    // בדיקת תקינות הקטלוג
    if (!catalog || !Array.isArray(catalog) || catalog.length === 0) {
      console.error('Catalog is not loaded or empty');
      return;
    }

    const appConfig = catalog.find(app => app.componentKey === widget.componentKey);
    if (!appConfig) return;
    setCurrentWidgetConfig(appConfig);

    setEditingWidget(widget);
    setIsEditMode(true);

    // Open unified WidgetConfigurator for all widget types
    setConfiguratorKey(widget.componentKey);
    setConfiguratorInitialData({
      props: widget.props || {},
      widgetSettings: widget.widgetSettings || {},
      templateId: widget.templateId || appConfig.templates?.[0]?.id || '',
    });
    setConfiguratorOpen(true);
  };

  const handleSaveToArchive = async () => {
    if (!widgetToDelete) return;

    // מצא את הווידג'ט שרוצים למחוק
    const widgetToSave = widgets.find(w => w.widgetId === widgetToDelete);
    if (!widgetToSave) return;

    // בדיקת מגבלת רכיבים שמורים לפי רישיון
    const savedWidgetsLimit = getEffectiveLimit(reduxPlanDefs?.[reduxLicense?.plan], reduxLicense, 'savedWidgets');
    const currentSavedCount = organization?.savedWidgets?.length ?? 0;
    if (!isUnlimited(savedWidgetsLimit) && currentSavedCount >= savedWidgetsLimit) {
      setDeleteConfirmOpen(false);
      setWidgetToDelete(null);
      dispatch({ type: 'OPEN_UPGRADE_DRAWER', payload: { featureName: 'ספריית רכיבים שמורים', requiredPlan: 'pro' } });
      return;
    }

    // הוסף ל-savedWidgets (ארכיון ברמת ארגון)
    dispatch({ type: 'ADD_TO_SAVED_WIDGETS_LOCAL', payload: { widget: widgetToSave } });

    // הסר מהדשבורד
    dispatch(removeWidget(widgetToDelete));

    setDeleteConfirmOpen(false);
    setWidgetToDelete(null);
  };

  const handleDeletePermanently = async () => {
    if (!widgetToDelete) return;

    // מחק לצמיתות (רק מהדשבורד, לא שומר בשום מקום)
    dispatch(removeWidget(widgetToDelete));

    setDeleteConfirmOpen(false);
    setWidgetToDelete(null);
  };

  const handleDeleteClick = (widgetId) => {
    setWidgetToDelete(widgetId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setWidgetToDelete(null);
  };

  // פונקציה חדשה: פתיחת פופאפ להוספת ווידג'ט חדש
  const handleOpenWidgetConfig = useCallback((componentKey, templateId = null) => {
    // מניעת הוספה במצב צפייה
    if (isViewingVersion) {
      console.warn('Cannot add widget in public view mode');
      return;
    }

    // בדיקת תקינות הקטלוג
    if (!catalog || !Array.isArray(catalog) || catalog.length === 0) {
      console.error('Catalog is not loaded or empty');
      return;
    }

    // מצא את הקונפיגורציה של הווידג'ט מהקטלוג
    const appConfig = catalog.find(app => app.componentKey === componentKey);
    if (!appConfig) {
      console.error('Widget not found in catalog:', componentKey);
      return;
    }

    // בדוק אם יש מקום פנוי לפני פתיחת הוויזרד
    const { w, h } = appConfig.dashboardLayout || { w: 8, h: 10 };
    const pos = findFirstFreeSpace(widgets, COLS, maxRows, w, h);
    if (!pos) {
      setNoSpaceDialogOpen(true);
      return;
    }

    setCurrentWidgetConfig(appConfig);
    setEditingWidget(null);
    setIsEditMode(false);

    // פתח WidgetConfigurator להוספת רכיב חדש
    setConfiguratorKey(componentKey);
    setConfiguratorInitialData(null);
    setConfiguratorOpen(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgets, catalog, COLS, maxRows, isViewingVersion]); // dependencies for useCallback

  // ============================================
  // Widget Setup Wizard handlers
  // ============================================

  /**
   * buildPropsFromFormData — ממיר formData של הוויזרד ל-props של הרכיב
   */
  const buildPropsFromFormData = useCallback((key, formData) => {
    const base = { title: formData.title || '', showTitle: formData.showTitle ?? true };
    switch (key) {
      case 'ZmanimWidget':
        return {
          ...base,
          customTimes: formData.customTimes || [],
          visibleZmanim: formData.visibleZmanim || null,
          showInyaneiHayom: formData.showInyaneiHayom || false,
          inyaneiDisplayMode: formData.inyaneiDisplayMode || 'section',
          inyaneiEdah: formData.inyaneiEdah || 'ashkenaz',
          inyaneiCategories: formData.inyaneiCategories || null,
        };
      case 'ShabbatWidget':
        return {
          ...base,
          customTimes: formData.shabbatPrayers || formData.customTimes || [],
          drashot: formData.drashot || [],
          entryIcon: formData.entryZman?.icon || formData.entryIcon || 'ShabbatCandles',
          exitIcon:  formData.exitZman?.icon  || formData.exitIcon  || 'MoonStar',
          entryZman: formData.entryZman || null,
          exitZman:  formData.exitZman  || null,
          showTimeRule: formData.showTimeRule || false,
          showEntryExit: formData.showEntryExit ?? true,
          showLeyning: formData.showLeyning || false,
          showHaftarah: formData.showHaftarah || false,
          leyningDisplayMode: formData.leyningDisplayMode || 'summary',
        };
      case 'MoadWidget':
        return {
          ...base,
          days:            formData.days || [],
          customTimes:     formData.moadPrayers || [],
          drashot:         formData.moadDrashot || [],
          entryIcon:       formData.entryIcon || 'ShabbatCandles',
          exitIcon:        formData.exitIcon  || 'MoonStar',
          globalEntryZman: formData.entryZman || null,
          globalExitZman:  formData.exitZman  || null,
          showLeyning:        formData.showLeyning || false,
          showHaftarah:       formData.showHaftarah || false,
          leyningDisplayMode: formData.leyningDisplayMode || 'summary',
        };
      case 'InyaneiHaYomWidget':
        return {
          ...base,
          edah:                    formData.edah || 'ashkenaz',
          showTachanun:            formData.showTachanun ?? true,
          showHallel:              formData.showHallel ?? true,
          showPrayerAdditions:     formData.showPrayerAdditions ?? true,
          showSeasonalPrayers:     formData.showSeasonalPrayers ?? true,
          showSeasonalTransitions: formData.showSeasonalTransitions ?? true,
          showSelichot:            formData.showSelichot ?? true,
          showWeekdayPrayers:      formData.showWeekdayPrayers ?? true,
          showShabbatAdditions:    formData.showShabbatAdditions ?? true,
          showMotzaeiShabbat:      formData.showMotzaeiShabbat ?? true,
          showSeasonalCustoms:     formData.showSeasonalCustoms ?? true,
          showLiturgy:             formData.showLiturgy ?? true,
          showLeyning:             formData.showLeyning || false,
          showHaftarah:            formData.showHaftarah || false,
          showMourningPeriods:     formData.showMourningPeriods ?? true,
          showLimudim:             formData.showLimudim ?? false,
          lookaheadDays:           formData.lookaheadDays ?? 0,
        };
      case 'LimudimWidget':
        return {
          ...base,
          showDafYomi:                   formData.showDafYomi ?? true,
          showMishnaYomi:                formData.showMishnaYomi ?? true,
          showNachYomi:                  formData.showNachYomi ?? false,
          showRambam1:                   formData.showRambam1 ?? true,
          showRambam3:                   formData.showRambam3 ?? false,
          showYerushalmiVilna:           formData.showYerushalmiVilna ?? false,
          showYerushalmiSchottenstein:   formData.showYerushalmiSchottenstein ?? false,
          showChofetzChaim:              formData.showChofetzChaim ?? false,
          showShemiratHaLashon:          formData.showShemiratHaLashon ?? false,
          showSeferHaMitzvot:            formData.showSeferHaMitzvot ?? false,
          showKitzurShulchanAruch:       formData.showKitzurShulchanAruch ?? false,
          showArukhHaShulchanYomi:       formData.showArukhHaShulchanYomi ?? false,
          showPsalms:                    formData.showPsalms ?? false,
          showTanakhYomi:                formData.showTanakhYomi ?? false,
          showPirkeiAvotSummer:          formData.showPirkeiAvotSummer ?? false,
          showPerekYomi:                 formData.showPerekYomi ?? false,
          showDafWeekly:                 formData.showDafWeekly ?? false,
          showDirshuAmudYomi:            formData.showDirshuAmudYomi ?? false,
        };
      case 'TefilotWidget':
        return {
          ...base,
          customTimes:      formData.customTimes || [],
          recurringDisplay: formData.recurringDisplay || 'compact',
          showRuleMode:     formData.showRuleMode || false,
          timelineValidFrom: formData.timelineValidFrom || '',
          timelineValidTo:   formData.timelineValidTo   || '',
        };
      case 'TextWidget':
        return { text: formData.text || '' };
      case 'ImageWidget':
        return { imageUrl: formData.imageUrl || '' };
      case 'DocumentWidget':
        return {
          documentUrl: formData.documentUrl || '',
          documentType: formData.documentType || '',
          fileName: formData.fileName || '',
          pageNumber: formData.pageNumber || 1,
          autoScroll: formData.autoScroll || false,
          scrollInterval: formData.scrollInterval || 10,
        };
      case 'WeatherWidget':
        return base;
      case 'NoticeBoardWidget':
        return { ...base, data: { notices: formData.notices || [] } };
      case 'EmergencyWidget':
        return { ...base, data: { volunteers: formData.emergencyData?.volunteers || formData.volunteers || { medic: [], paramedic: [], maar: [] } } };
      case 'ContactsWidget':
        return { ...base, data: { contacts: formData.contacts || [] } };
      case 'SimchaWidget':
        return {
          ...base,
          data: {
            simchas:              formData.simchas              || [],
            showTopGreeting:      formData.showTopGreeting      ?? false,
            topGreeting:          formData.topGreeting          || 'הקהילה מאחלת מזל טוב לכל בעלי השמחות!',
            topBgColor:           formData.topBgColor           || '',
            topTextColor:         formData.topTextColor         || '',
            topFontFamily:        formData.topFontFamily        || 'inherit',
            topFontSize:          formData.topFontSize          || 'medium',
            topSymbol:            formData.topSymbol            !== undefined ? formData.topSymbol : '✦',
            showBottomGreeting:   formData.showBottomGreeting   ?? false,
            bottomGreeting:       formData.bottomGreeting       || 'ויזכו לאורך ימים ושנים טובות, אמן!',
            bottomBgColor:        formData.bottomBgColor        || '',
            bottomTextColor:      formData.bottomTextColor      || '',
            bottomFontFamily:     formData.bottomFontFamily     || 'inherit',
            bottomFontSize:       formData.bottomFontSize       || 'medium',
            bottomSymbol:         formData.bottomSymbol         !== undefined ? formData.bottomSymbol : '✦',
          },
        };
      case 'NitzavSpotlightWidget':
        return {
          ...base,
          data: {
            name:       formData.nitzavData?.name       || formData.nitzavName       || '',
            type:       formData.nitzavData?.type       || formData.nitzavType       || 'נציב היום',
            dedication: formData.nitzavData?.dedication || formData.nitzavDedication || '',
            iconId:     formData.nitzavData?.iconId     || formData.nitzavIconId     || 'star',
          },
        };
      case 'ExpensesAnalyticsWidget':
        return { ...base, data: { categories: formData.categories || [] }, freeText: formData.freeText || '' };
      case 'FinancialGaugeWidget':
        return { ...base, data: { incomeData: formData.incomeData || [], expensesData: formData.expensesData || [] } };
      case 'ParnasWidget':
        return {
          ...base,
          sponsors:              formData.sponsors              || [],
          parnasType:            formData.parnasType            || 'maor',
          parnasTypeCustomLabel: formData.parnasTypeCustomLabel || '',
          parnasTypeIcon:        formData.parnasTypeIcon        || 'star',
          tileColors:            formData.tileColors            || undefined,
          placeholderName:       formData.placeholderName       || undefined,
          placeholderNote:       formData.placeholderNote       || undefined,
          weeklyYearDisplayYear: formData.weeklyYearDisplayYear ?? null,
          weeksAheadCount:       formData.weeksAheadCount       ?? 8,
          honorCardPriority:     formData.honorCardPriority     || 'auto',
          includedSlotTypes:     formData.includedSlotTypes     || undefined,
        };
      case 'OmerWidget':
        return {
          ...base,
          nusach:            formData.nusach            || 'ashkenaz',
          showBracha:        formData.showBracha        ?? true,
          showFullNusach:    formData.showFullNusach     ?? true,
          showLeshemYichud:  formData.showLeshemYichud   ?? false,
          showPasuk:         formData.showPasuk          || 'none',
          showAfterCount:    formData.showAfterCount      ?? false,
          showPreview:       formData.showPreview         ?? false,
        };
      case 'HalachicTimesWidget':
        return {
          ...base,
          visibleZmanim: formData.visibleZmanim || null,
        };
      case 'SeatingMapWidget':
        return { ...base, mapData: formData.mapData || null };
      default:
        return base;
    }
  }, []);

  /**
   * handleWizardFinish — קולט formData + templateId מהוויזרד, יוצר רכיב ומוסיף ללוח
   */
  /**
   * handleFetchZmanimForDay — fetches halachic times for a specific Hebrew date
   * @param {number} hebrewMonth - Hebrew month number (1=Nisan, 7=Tishrei, ...)
   * @param {number} hebrewDay   - Hebrew day of month
   * @returns {Promise<object>}  - zmanim times object from Hebcal API
   */
  const handleFetchZmanimForDay = useCallback(async (hebrewMonth, hebrewDay) => {
    const hebrewYear = hebrewDate?.hy;
    const lat = organization?.location?.lat;
    const lng = organization?.location?.lng;
    if (!hebrewYear || !lat || !lng) return {};
    const MONTH_NAMES = {
      1:'Nisan', 2:'Iyar', 3:'Sivan', 4:'Tamuz', 5:'Av', 6:'Elul',
      7:'Tishrei', 8:'Cheshvan', 9:'Kislev', 10:'Tevet', 11:'Shvat',
      12:'Adar1', 13:'Adar2',
    };
    const monthName = MONTH_NAMES[hebrewMonth];
    if (!monthName) return {};
    const tzid = organization?.location?.timezone || 'Asia/Jerusalem';
    // חילוץ זמן אמין — regex ישיר מ-ISO (אין תלות ב-locale), fallback ל-Intl
    const fmtTime = (iso) => {
      if (!iso) return '';
      try {
        if (typeof iso === 'string' && iso.includes('T')) {
          const m = iso.match(/T(\d{2}):(\d{2})/);
          if (m) return `${m[1]}:${m[2]}`;
        }
        const d = iso instanceof Date ? iso : new Date(iso);
        if (isNaN(d.getTime())) return '';
        const parts = new Intl.DateTimeFormat('en-GB', {
          hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Jerusalem',
        }).formatToParts(d);
        const h = parts.find(p => p.type === 'hour')?.value || '';
        const mn = parts.find(p => p.type === 'minute')?.value || '';
        return h && mn ? `${h.padStart(2, '0')}:${mn.padStart(2, '0')}` : '';
      } catch { return ''; }
    };
    const todayStr = new Date().toISOString().slice(0, 10);
    try {
      // Step 1: Convert Hebrew → Gregorian; auto-advance to next year if date already passed
      const convert = async (hy) => {
        const r = await fetch(`https://www.hebcal.com/converter?cfg=json&hy=${hy}&hm=${monthName}&hd=${hebrewDay}&h2g=1`);
        return r.json();
      };
      let convData = await convert(hebrewYear);
      let dateStr = `${convData.gy}-${String(convData.gm).padStart(2,'0')}-${String(convData.gd).padStart(2,'0')}`;
      if (dateStr < todayStr) {
        convData = await convert(hebrewYear + 1);
        dateStr = `${convData.gy}-${String(convData.gm).padStart(2,'0')}-${String(convData.gd).padStart(2,'0')}`;
      }

      // חישוב תאריך ערב (יום לפני) — לשליפת אירועי לוח שנה שמופיעים בערב (הדלקת נרות / תחילת צום)
      const [gy, gm, gd] = dateStr.split('-').map(Number);
      const eveObj = new Date(gy, gm - 1, gd - 1);
      const eveDateStr = `${eveObj.getFullYear()}-${String(eveObj.getMonth()+1).padStart(2,'0')}-${String(eveObj.getDate()).padStart(2,'0')}`;

      // Step 2+3: זמנים + אירועי לוח שנה ליום ולערב — כולם במקביל
      const calendarParams = `v=1&cfg=json&maj=on&mf=on&c=on&geo=pos&latitude=${lat}&longitude=${lng}&tzid=${tzid}`;
      const zmUrl = `https://www.hebcal.com/zmanim?cfg=json&geo=pos&sec=1&latitude=${lat}&longitude=${lng}&tzid=${tzid}`;
      const [zmRes, calRes, calEveRes, zmEveRes] = await Promise.all([
        fetch(`${zmUrl}&date=${dateStr}`),
        fetch(`https://www.hebcal.com/hebcal?${calendarParams}&start=${dateStr}&end=${dateStr}`),
        fetch(`https://www.hebcal.com/hebcal?${calendarParams}&start=${eveDateStr}&end=${eveDateStr}`),
        fetch(`${zmUrl}&date=${eveDateStr}`),
      ]);
      const zmData = await zmRes.json();
      const rawTimes = zmData.times || {};

      // חישוב זמני חמץ מ-ISO גולמי לפני פורמט (נדרש חישוב ממוצע) — מג"א 19.8° (מחמיר)
      const sofZmanAchilatChametz_iso = rawTimes.sofZmanTfilla;
      const sofZmanBiurChametz_iso = (rawTimes.sofZmanTfilla && rawTimes.chatzot)
        ? new Date((new Date(rawTimes.sofZmanTfilla).getTime() + new Date(rawTimes.chatzot).getTime()) / 2)
        : null;
      const sofZmanAchilatChametzMGA_iso = rawTimes.sofZmanTfillaMGA19Point8;
      const sofZmanBiurChametzMGA_iso = (rawTimes.sofZmanTfillaMGA19Point8 && rawTimes.chatzot)
        ? new Date((new Date(rawTimes.sofZmanTfillaMGA19Point8).getTime() + new Date(rawTimes.chatzot).getTime()) / 2)
        : null;

      // פורמט כל הזמנים ל-HH:MM וסידור מפתחות + override מג"א ל-19.8° (מחמיר)
      const times = {};
      Object.entries(rawTimes).forEach(([k, v]) => { times[k] = v ? fmtTime(v) : v; });
      if (times.sofZmanShmaMGA19Point8)   times.sofZmanShmaMGA   = times.sofZmanShmaMGA19Point8;
      if (times.sofZmanTfillaMGA19Point8) times.sofZmanTfillaMGA = times.sofZmanTfillaMGA19Point8;

      // נרמול שמות מפתחות: Hebcal API → שמות פנימיים של ZmanSelect
      if (rawTimes.sunrise)     times.netzHachama  = (() => { const m = rawTimes.sunrise.match(/T(\d{2}):(\d{2}):(\d{2})/); return m ? `${m[1]}:${m[2]}:${m[3]}` : fmtTime(rawTimes.sunrise); })();
      if (rawTimes.plagHaMincha) times.plagHamincha = fmtTime(rawTimes.plagHaMincha);

      // זמני חמץ (מחושבים — לא מוחזרים ישירות מ-Hebcal)
      if (sofZmanAchilatChametz_iso)    times.sofZmanAchilatChametz    = fmtTime(sofZmanAchilatChametz_iso);
      if (sofZmanBiurChametz_iso)       times.sofZmanBiurChametz       = fmtTime(sofZmanBiurChametz_iso);
      if (sofZmanAchilatChametzMGA_iso) times.sofZmanAchilatChametzMGA = fmtTime(sofZmanAchilatChametzMGA_iso);
      if (sofZmanBiurChametzMGA_iso)    times.sofZmanBiurChametzMGA    = fmtTime(sofZmanBiurChametzMGA_iso);

      // פיענוח אירועי לוח שנה של היום עצמו — שמורים כ-ISO לטובת formatApiTime ב-extraOptions
      const calData = await calRes.json();
      (calData.items || []).forEach(item => {
        if (!item.date?.includes('T')) return;
        if (item.date.substring(0, 10) !== dateStr) return;
        if (item.category === 'candles') times.candleLighting = fmtTime(item.date);
        else if (item.category === 'havdalah') times.havdalah = fmtTime(item.date);
        else if (item.category === 'zmanim' && item.subcat === 'fast') {
          const t = item.title?.toLowerCase() || '';
          if (t.includes('firstborn')) return; // תענית בכורות — לא תענית ציבור, לא רלוונטי לרכיב מועד
          if (t.includes('begins')) times.fastBegins = fmtTime(item.date);
          else times.fastEnds = fmtTime(item.date);
        }
      });

      // פיענוח אירועי ערב — הדלקת נרות / תחילת צום ששייכים ליום זה אך מופיעים בלוח של הערב (כגון: יום כיפור)
      const calEveData = await calEveRes.json();
      (calEveData.items || []).forEach(item => {
        if (!item.date?.includes('T')) return;
        if (item.date.substring(0, 10) !== eveDateStr) return;
        if (item.category === 'candles' && !times.eveCandleLighting)
          times.eveCandleLighting = fmtTime(item.date);
        else if (item.category === 'zmanim' && item.subcat === 'fast' && item.title?.toLowerCase().includes('begins') && !times.eveFastBegins)
          times.eveFastBegins = fmtTime(item.date);
      });

      // שקיעת ערב המועד — try/catch נפרד כדי שכשל כאן לא יפגע בשאר הנתונים
      try {
        const zmEveData = await zmEveRes.json();
        if (zmEveData.times?.sunset) times.eveSunset = fmtTime(zmEveData.times.sunset);
      } catch { /* eveSunset לא זמין — לא קריטי */ }

      return times;
    } catch {
      return {};
    }
  }, [hebrewDate, organization]);

  // שליפת זמני היום הנוכחי — לתצוגה בדרופדאון ZmanSelect
  const handleFetchTodayZmanim = useCallback(async (overrideDateStr) => {
    const loc = organization?.location;
    if (!loc?.lat || !loc?.lng) return {};
    const lat = loc.lat;
    const lng = loc.lng;
    const tzid = loc.timezone || 'Asia/Jerusalem';
    let dateStr = overrideDateStr;
    if (!dateStr) {
      const now = new Date();
      dateStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    }
    try {
      const res = await fetch(`https://www.hebcal.com/zmanim?cfg=json&geo=pos&sec=1&latitude=${lat}&longitude=${lng}&tzid=${tzid}&date=${dateStr}`);
      const data = await res.json();
      const times = data.times || {};
      // שימוש בגרסת 19.8° למג"א — המחמירה יותר
      if (times.sofZmanShmaMGA19Point8)   times.sofZmanShmaMGA   = times.sofZmanShmaMGA19Point8;
      if (times.sofZmanTfillaMGA19Point8) times.sofZmanTfillaMGA = times.sofZmanTfillaMGA19Point8;
      // פורמט שעה HH:MM — regex ישיר מ-ISO (אמין, ללא תלות ב-locale)
      const fmt = (iso) => {
        if (!iso) return '';
        try {
          if (typeof iso === 'string' && iso.includes('T')) {
            const m = iso.match(/T(\d{2}):(\d{2})/);
            if (m) return `${m[1]}:${m[2]}`;
          }
          return '';
        } catch { return ''; }
      };
      const formatted = {};
      Object.entries(times).forEach(([k, v]) => { formatted[k] = v ? fmt(v) : v; });
      // נרמול שמות מפתחות: Hebcal API → שמות פנימיים של ZmanSelect
      if (times.sunrise)      formatted.netzHachama  = (() => { const m = times.sunrise.match(/T(\d{2}):(\d{2}):(\d{2})/); return m ? `${m[1]}:${m[2]}:${m[3]}` : fmt(times.sunrise); })();
      if (times.plagHaMincha) formatted.plagHamincha = fmt(times.plagHaMincha);
      return formatted;
    } catch {
      return {};
    }
  }, [organization]);

  // מחשב תאריך של שישי הקרוב (כולל שישי הנוכחי אם היום שישי)
  const getNextFridayDateStr = useCallback(() => {
    const now = new Date();
    const day = now.getDay(); // 0=Sun … 5=Fri … 6=Sat
    // אם היום שישי — השתמש בתאריך של היום; אם שבת — שישי הבא בעוד 6 ימים; אחרת — שישי הקרוב
    const daysUntilFriday = day === 5 ? 0 : (5 - day + 7) % 7 || 7;
    const friday = new Date(now);
    friday.setDate(now.getDate() + daysUntilFriday);
    return `${friday.getFullYear()}-${String(friday.getMonth()+1).padStart(2,'0')}-${String(friday.getDate()).padStart(2,'0')}`;
  }, []);

  const handleFetchFridayZmanim = useCallback(async () => {
    return handleFetchTodayZmanim(getNextFridayDateStr());
  }, [handleFetchTodayZmanim, getNextFridayDateStr]);

  // ── Unified Configurator save handler ──
  const handleConfiguratorSave = useCallback((componentKey, props, templateId, widgetSettings) => {
    if (!currentWidgetConfig) return;

    // Edit mode — update existing widget
    if (isEditMode && editingWidget) {
      const updatedWidget = {
        ...editingWidget,
        templateId: templateId || currentWidgetConfig.templates?.[0]?.id || '',
        props,
        widgetSettings: widgetSettings || { bgColor: '', borderType: 'default' },
      };
      dispatch(updateWidget(editingWidget.widgetId, updatedWidget));
    } else {
      // Add mode — find free space and create new widget
      const { w, h } = currentWidgetConfig.dashboardLayout || { w: 8, h: 10 };
      const pos = findFirstFreeSpace(widgets, COLS, maxRows, w, h);
      if (!pos) {
        setConfiguratorOpen(false);
        setNoSpaceDialogOpen(true);
        return;
      }
      const newWidget = {
        widgetId: uuidv4(),
        appId: currentWidgetConfig.appId,
        componentKey,
        templateId: templateId || currentWidgetConfig.templates?.[0]?.id || '',
        dashboardLayout: { x: pos.x, y: pos.y, w, h },
        props,
        widgetSettings: widgetSettings || { bgColor: '', borderType: 'default' },
      };
      dispatch(addWidget(newWidget));
    }

    setConfiguratorOpen(false);
    setConfiguratorKey(null);
    setConfiguratorInitialData(null);
    setCurrentWidgetConfig(null);
    if (isEditMode) {
      setEditingWidget(null);
      setIsEditMode(false);
    }
  }, [currentWidgetConfig, isEditMode, editingWidget, widgets, COLS, maxRows, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // Handlers for AddWidgetButton - 3 סוגי הוספה
  // ============================================

  /**
   * handleSelectWidget - בחירת ווידג'ט מהקטלוג
   * בודק אם יש מקום, יוצר ווידג'ט חדש ומוסיף לדשבורד
   */
  const handleSelectWidget = useCallback(async (componentKey, templateId, dashboardLayout) => {
    if (isViewingVersion) {
      console.warn('Cannot add widget in public view mode');
      return { success: false };
    }

    const appConfig = catalog.find(app => app.componentKey === componentKey);
    if (!appConfig) {
      console.error('Widget not found in catalog:', componentKey);
      return { success: false };
    }

    // בדוק אם יש מקום פנוי
    const { w, h } = dashboardLayout || appConfig.dashboardLayout || { w: 8, h: 10 };
    const pos = findFirstFreeSpace(widgets, COLS, maxRows, w, h);

    if (!pos) {
      return { success: false }; // AddWidgetButton יציג דיאלוג
    }

    // צור ווידג'ט חדש
    const newWidget = {
      widgetId: uuidv4(),
      appId: appConfig.appId,
      componentKey: componentKey,
      templateId: templateId || appConfig.templates?.[0]?.id || '',
      dashboardLayout: { x: pos.x, y: pos.y, w, h },
      props: {},
      widgetSettings: { bgColor: '', borderType: 'default' }
    };

    dispatch(addWidget(newWidget));
    return { success: true };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewingVersion, catalog, widgets, COLS, maxRows, dispatch]);

  /**
   * handleCopyWidget - העתקת ווידג'ט מדשבורד אחר
   * מעתיק את כל ה-props, הגודל לפי APP_CATALOG
   */
  const handleCopyWidget = useCallback(async (widget) => {
    if (isViewingVersion) {
      console.warn('Cannot copy widget in public view mode');
      return { success: false };
    }

    // קרא את ה-widgets הנוכחיים ישירות מ-Redux store
    const currentDashboard = store.getState().dashboard.dashboard;
    const currentWidgets = currentDashboard?.current?.widgets || [];

    // מצא גודל מהקטלוג
    const catalogEntry = catalog.find(c => c.componentKey === widget.componentKey);
    const { w, h } = catalogEntry?.dashboardLayout || { w: 8, h: 10 };

    // בדוק אם יש מקום פנוי
    const pos = findFirstFreeSpace(currentWidgets, COLS, maxRows, w, h);
    if (!pos) {
      setNoSpaceDialogOpen(true);
      return { success: false };
    }

    // העתק עם מיקום חדש וגודל מהקטלוג
    // ⚠️ לא יוצרים widgetId חדש כאן! זה יקרה ב-copyWidgetFromDashboard
    const newWidget = {
      ...widget,
      dashboardLayout: { x: pos.x, y: pos.y, w, h }
    };

    dispatch(copyWidgetFromDashboard(newWidget, catalog));
    return { success: true };
  }, [isViewingVersion, store, catalog, COLS, maxRows, dispatch]);

  /**
   * handleMoveWidget - גזירת ווידג'ט מרכיבים שמורים
   * מעביר ווידג'ט מ-savedWidgets לדשבורד, הגודל לפי APP_CATALOG
   */
  const handleMoveWidget = useCallback(async (widget) => {
    if (isViewingVersion) {
      console.warn('Cannot move widget in public view mode');
      return { success: false };
    }

    // קרא את ה-widgets הנוכחיים ישירות מ-Redux store
    const currentDashboard = store.getState().dashboard.dashboard;
    const currentWidgets = currentDashboard?.current?.widgets || [];

    // מצא גודל מהקטלוג
    const catalogEntry = catalog.find(c => c.componentKey === widget.componentKey);
    const { w, h } = catalogEntry?.dashboardLayout || { w: 8, h: 10 };

    // בדוק אם יש מקום פנוי
    const pos = findFirstFreeSpace(currentWidgets, COLS, maxRows, w, h);
    if (!pos) {
      setNoSpaceDialogOpen(true);
      return { success: false };
    }

    // העבר עם מיקום חדש וגודל מהקטלוג
    const newWidget = {
      ...widget,
      dashboardLayout: { x: pos.x, y: pos.y, w, h }
      // לא יוצרים widgetId חדש כאן! זה יקרה ב-moveWidgetFromSaved
    };

    dispatch(moveWidgetFromSaved(newWidget, catalog));

    return { success: true };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewingVersion, store, catalog, COLS, maxRows, screenId, dispatch]);


  // ============================================
  // העברת handlers ל-DashboardEditor
  // ============================================
  useEffect(() => {
    if (onHandlersReady && !isViewingVersion) {
      onHandlersReady({
        handleSelectWidget,
        handleCopyWidget,
        handleMoveWidget,
        handleOpenWidgetConfig
      });
    }
  }, [onHandlersReady, isViewingVersion, handleSelectWidget, handleCopyWidget, handleMoveWidget, handleOpenWidgetConfig]);

  // This function is called during drag/resize - we don't update Redux here anymore
  const handleLayoutChange = (newLayout) => {
    // Just update the visual layout, no Redux update
    // Redux will be updated only on drag/resize stop
  };

  // Called when drag stops - update Redux only here
  const handleDragStop = (newLayout) => {
    updateLayoutInRedux(newLayout);
  };

  // Called when resize stops - update Redux only here
  const handleResizeStop = (newLayout) => {
    updateLayoutInRedux(newLayout);
  };

  // Helper function to update Redux with new layout
  const updateLayoutInRedux = (newLayout) => {
    // במצב צפייה ציבורית - אל תעדכן כלום
    if (isViewingVersion) {
      console.log('⚠️ Cannot update layout in public view mode');
      return;
    }

    // מצא את הווידג'ט שבאמת השתנה
    const changedItems = [];

    widgets.forEach(widget => {
      const layoutItem = newLayout.find(l => l.i === widget.widgetId);
      if (!layoutItem) return;

      const currentLayout = widget.dashboardLayout;
      const hasChanged = (
        layoutItem.x !== currentLayout.x ||
        layoutItem.y !== currentLayout.y ||
        layoutItem.w !== currentLayout.w ||
        layoutItem.h !== currentLayout.h
      );

      if (hasChanged) {
        changedItems.push({
          ...widget,
          dashboardLayout: {
            x: layoutItem.x,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h
          }
        });
      }
    });

    // אם אין שינויים, אל תעדכן כלום
    if (changedItems.length === 0) return;

    // עדכן רק את הווידג'טים שהשתנו
    const updatedWidgets = widgets.map(widget => {
      const changed = changedItems.find(c => c.widgetId === widget.widgetId);
      return changed || widget; // אם השתנה - קח את החדש, אחרת קח את הישן
    });

    dispatch(updateDashboardLocal(updatedWidgets, screenId));
  };



  const renderWidget = (widget) => {
    // Find template name from catalog using templateId
    let templateName = null;

    if (widget.templateId && catalog && Array.isArray(catalog)) {
      const catalogEntry = catalog.find(entry =>
        entry.templates && entry.templates.some(t => t.id === widget.templateId)
      );

      if (catalogEntry) {
        const template = catalogEntry.templates.find(t => t.id === widget.templateId);
        if (template) {
          templateName = template.name;
        }
      }
    }

    // אם לא מצאנו template name, השתמש בברירת מחדל לפי סוג הווידג'ט
    if (!templateName) {
      const defaultTemplates = {
        'ZmanimWidget': 'Timeline',
        'WeatherWidget': 'Card',
        'ShabbatWidget': 'Card',
        'MoadWidget': 'Modern',
        'TefilotWidget': 'Modern',
        'TextWidget': 'Simple',
        'ImageWidget': 'Fixed',
        'NoticeBoardWidget': 'Board',
        'EmergencyWidget': 'List',
        'ContactsWidget': 'List',
        'SimchaWidget': 'Cards',
        'NitzavSpotlightWidget': 'Crown',
        'ParnasWidget': 'Classic',
        'OmerWidget': 'Elegant',
        'ExpensesAnalyticsWidget': 'Pie',
        'FinancialGaugeWidget': 'Gauge',
        'ClockWidget': 'GoldenSynagogue',
        'NextMinyaWidget': 'Card',
      };
      // אם templateId הוא שם תבנית ישיר (כמו 'Classic') ולא UUID מ-Firestore — נשתמש בו.
      // UUID מ-Firestore שהחיפוש בקטלוג נכשל עבורו לא אמור לשמש כשם תבנית.
      const isFirestoreUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const directTemplateName = widget.templateId && !isFirestoreUUID.test(widget.templateId) ? widget.templateId : null;
      templateName = directTemplateName || defaultTemplates[widget.componentKey] || 'Default';
    }

    // ── License enforcement: show placeholder if widget is blocked for this org ──
    const catalogEntryForLock = catalog.find(app => app.componentKey === widget.componentKey);
    if (catalogEntryForLock && reduxLicense) {
      const inBlock = reduxLicense.overrides?.widgetOverrides?.block?.includes(widget.componentKey);
      if (inBlock) {
        return (
          <LockedWidgetPlaceholder
            widgetName={catalogEntryForLock.name}
            requiredPlan="blocked"
          />
        );
      }
    }

    switch (widget.componentKey) {
      case 'ZmanimWidget': {
        // מצא את הווידג'ט של שבת כדי לקבל את הזמנים העצמאיים שלו
        const shabbatWidget = widgets.find(w => w.componentKey === 'ShabbatWidget');
        const shabbatCustomTimes = shabbatWidget?.props?.customTimes || [];

        // חשב האם עברנו את שקיעת החמה/צאת הכוכבים היום (לפילטור מועדים)
        const _nowMinsForMoad = new Date().getHours() * 60 + new Date().getMinutes();
        const _nightfallStr = zmanim?.tzeit85deg || zmanim?.sunset;
        const _nightfallParts = _nightfallStr ? _nightfallStr.split(':').map(Number) : null;
        const _nightfallMins = (_nightfallParts && !isNaN(_nightfallParts[0]) && !isNaN(_nightfallParts[1]))
          ? _nightfallParts[0] * 60 + _nightfallParts[1]
          : null;
        const isPastNightfallToday = _nightfallMins != null
          ? _nowMinsForMoad > _nightfallMins
          : _nowMinsForMoad > 18 * 60; // fallback: 18:00 אם אין נתוני שקיעה


        // צבעי מקורות תפילה
        const srcTefilotColor = widget.props?.tefilotColor || '#0369a1';
        const srcMoadColor = widget.props?.moadColor || '#b45309';
        const srcShabbatColor = widget.props?.shabbatColor || '#7c3aed';

        // אסוף זמני תפילה מ-TefilotWidget ופתח אותם לפורמט ZmanimWidget
        const tefilotWidgetInstances = widgets.filter(w => w.componentKey === 'TefilotWidget');
        const tefilotResolvedForZmanim = (() => {
          const PRAYER_LABELS = { shacharit: 'שחרית', mincha: 'מנחה', maariv: 'מעריב' };
          const toMins = (t) => { if (!t || t === '--:--') return -1; const [h, m] = t.split(':').map(Number); return h * 60 + m; };
          const fromMins = (mins) => `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
          const todayDow = new Date().getDay();
          const DOW_TO_DAY_KEY = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
          const todayDayKey = DOW_TO_DAY_KEY[todayDow];
          const todayHebrewDay = (() => { try { return parseInt(new Intl.DateTimeFormat('he-u-ca-hebrew', { day: 'numeric' }).format(new Date())); } catch { return 0; } })();
          const isRoshChodesh = todayHebrewDay === 1 || todayHebrewDay === 30;

          const resolveRelative = (baseKey, offset, roundTo, zmanimData) => {
            const map = {
              daySunset: zmanimData?.sunset, dayAlot: zmanimData?.alotHaShachar, dayNetz: zmanimData?.netzHachama,
              dayChatzot: zmanimData?.chatzot, daySofZmanShma: zmanimData?.sofZmanShma, daySofZmanShmaMGA: zmanimData?.sofZmanShmaMGA,
              daySofZmanTfilla: zmanimData?.sofZmanTfilla, daySofZmanTfillaMGA: zmanimData?.sofZmanTfillaMGA,
              dayMinchaGedola: zmanimData?.minchaGedola, dayMinchaKetana: zmanimData?.minchaKetana,
              dayPlagHamincha: zmanimData?.plagHamincha,
              dayTzeit85: zmanimData?.tzeit85deg, dayTzeit7083: zmanimData?.tzeit7083deg,
              dayTzeit42: zmanimData?.tzeit42min, dayTzeit50: zmanimData?.tzeit50min, dayTzeit72: zmanimData?.tzeit72min,
            };
            const base = map[baseKey];
            if (!base) return '--:--';
            let total = toMins(base) + (parseInt(offset) || 0);
            total = ((total % 1440) + 1440) % 1440;
            if (roundTo > 0) { const m = total % 60; total = Math.floor(total / 60) * 60 + Math.floor(m / roundTo) * roundTo; }
            return fromMins(total);
          };

          const resolveDesc = (desc, zmanimData) => {
            if (!desc) return '--:--';
            if (desc.type === 'fixed') return desc.time || '--:--';
            if (desc.type === 'relative') return resolveRelative(desc.baseTime, desc.offset, desc.roundTo || 0, zmanimData);
            return '--:--';
          };

          const result = [];
          const _tefilotToday = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
          tefilotWidgetInstances.forEach(tw => {
            // בדיקת תוקף תאריכי הזרקה לציר הזמן
            const _tvFrom = tw.props?.timelineValidFrom;
            const _tvTo   = tw.props?.timelineValidTo;
            if (_tvFrom && _tefilotToday < _tvFrom) return; // עוד לא הגיע תאריך ההתחלה
            if (_tvTo   && _tefilotToday > _tvTo)   return; // עבר תאריך הסיום

            const zmanimData = engineZmanim || tefilotByWidgetId[tw.widgetId]?.data?.zmanim;
            (tw.props?.customTimes || []).forEach(ct => {
              const label = ct.label || PRAYER_LABELS[ct.prayer] || '';
              if (!label) return;

              // סינון לפי ימים — אם מוגדרים ימים ספציפיים, הצג רק ביום הרלוונטי
              if (ct.days?.length && !ct.days.includes(todayDayKey)) return;

              const override = (ct.dayOverrides || []).find(ov => {
                const days = ov.days || [];
                return days.includes(todayDow) || (isRoshChodesh && days.includes('roshChodesh'));
              });

              if (ct.type === 'recurring') {
                const startDesc = ct.startType === 'relative'
                  ? { type: 'relative', baseTime: ct.startBaseTime, offset: ct.startOffset, roundTo: ct.startRoundTo }
                  : { type: 'fixed', time: ct.startTime };
                const endDesc = ct.endType === 'relative'
                  ? { type: 'relative', baseTime: ct.endBaseTime, offset: ct.endOffset, roundTo: ct.endRoundTo }
                  : { type: 'fixed', time: ct.endTime };
                const start = toMins(resolveDesc(startDesc, zmanimData));
                const end = toMins(resolveDesc(endDesc, zmanimData));
                const iv = parseInt(ct.interval) || 30;
                if (start >= 0 && end >= 0) {
                  for (let t = start; t <= end; t += iv) {
                    result.push({ label, time: fromMins(t), type: 'highlighted', accentColor: srcTefilotColor });
                  }
                }
              } else {
                let time;
                if (override) {
                  time = resolveDesc({ type: override.type, time: override.time, baseTime: override.baseTime, offset: override.offset, roundTo: override.roundTo }, zmanimData);
                } else {
                  time = ct.type === 'fixed'
                    ? (ct.time || '--:--')
                    : resolveRelative(ct.baseTime, ct.offset, ct.roundTo || 0, zmanimData);
                }
                if (time && time !== '--:--') result.push({ label, time, type: 'highlighted', accentColor: srcTefilotColor, isOverride: !!override });
              }
            });
          });
          return result;
        })();

        // אסוף זמני מועד מ-MoadWidget ופתח אותם לפורמט ZmanimWidget (3 ימים קדימה)
        const _moadLookaheadDates = [0, 1, 2].map(i => {
          const d = new Date(); d.setDate(d.getDate() + i);
          return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        });
        const moadResolvedForZmanim = (() => {
          const result = [];
          const lookaheadDates = _moadLookaheadDates;

          widgets.filter(w => w.componentKey === 'MoadWidget').forEach(mw => {
            const days = moadByWidgetId[mw.widgetId]?.data?.days || mw.props?.days || [];
            const customTimes = mw.props?.customTimes || [];

            lookaheadDates.forEach((dateStr, daysOffset) => {
              const moadDay = days.find(d => d.date === dateStr);
              if (!moadDay) return;

              // אם זה היום הנוכחי — בדוק האם עברנו את צאת הכוכבים של יום המועד הזה
              // (exitTime = tzeit של היום עצמו = הגבול בין כב׳ לכג׳ אדר וכד׳)
              if (daysOffset === 0) {
                const exitStr = moadDay.exitTime;
                if (exitStr && exitStr !== '--:--') {
                  const [eh, em] = exitStr.split(':').map(Number);
                  if (!isNaN(eh) && !isNaN(em) && _nowMinsForMoad > eh * 60 + em) return;
                } else if (isPastNightfallToday) {
                  return;
                }
              }
              const dayIdx = days.indexOf(moadDay);
              const moadZmanim = moadDay.zmanim || {};

              // מפתחות שבהגדרה שייכים לערב החג (לא ליום המועד עצמו)
              const EVE_SOURCE_KEYS = new Set(['dayEntrySunset', 'dayEntryTime']);

              const resolveMoadRelative = (baseKey, off, roundTo) => {
                const map = {
                  daySunset:      moadZmanim.sunset,         dayAlot:    moadZmanim.alotHaShachar,
                  dayTzeit85:     moadZmanim.tzeit85deg,     dayTzeit7083: moadZmanim.tzeit7083deg,
                  dayTzeit42:     moadZmanim.tzeit42min,     dayTzeit50: moadZmanim.tzeit50min,
                  dayTzeit72:     moadZmanim.tzeit72min,     dayChatzot: moadZmanim.chatzot,
                  daySofZmanShma: moadZmanim.sofZmanShma,    daySofZmanShmaMGA: moadZmanim.sofZmanShmaMGA,
                  dayEntryTime:   moadDay.entryTime,         dayExitTime:   moadDay.exitTime,
                  dayExitSunset:  moadZmanim.sunset,         // שקיעה ביציאת המועד = שקיעת יום המועד עצמו
                  dayEntrySunset: moadDay.eveSunset,         // שקיעה בכניסת המועד = שקיעת ערב החג
                  // מפתחות נוספים — חייבים להיות מסונכרנים עם baseTimeMap ב-MoadWidget.jsx
                  dayNetz:                     moadZmanim.netzHachama,
                  dayMinchaGedola:             moadZmanim.minchaGedola,
                  dayMinchaKetana:             moadZmanim.minchaKetana,
                  dayPlagHamincha:             moadZmanim.plagHamincha,
                  daySofZmanTfilla:            moadZmanim.sofZmanTfilla,
                  daySofZmanTfillaMGA:         moadZmanim.sofZmanTfillaMGA,
                  daySofZmanBiurChametz:       moadZmanim.sofZmanBiurChametz,
                  daySofZmanAchilatChametz:    moadZmanim.sofZmanAchilatChametz,
                  daySofZmanBiurChametzMGA:    moadZmanim.sofZmanBiurChametzMGA,
                  daySofZmanAchilatChametzMGA: moadZmanim.sofZmanAchilatChametzMGA,
                };
                const base = map[baseKey];
                if (!base) return '--:--';
                const toM = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
                const fromM = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
                let total = toM(base) + (parseInt(off) || 0);
                total = ((total % 1440) + 1440) % 1440;
                if (roundTo > 0) { const mins = total % 60; total = Math.floor(total / 60) * 60 + Math.floor(mins / roundTo) * roundTo; }
                return fromM(total);
              };

              // entryTime = צאת כוכבים של הלילה שלפני התאריך הלועזי (= תחילת יום המועד העברי)
              const entryMins = (() => {
                const et = moadDay.entryTime;
                if (!et || et === '--:--') return null;
                const [h, m] = et.split(':').map(Number);
                return !isNaN(h) && !isNaN(m) ? h * 60 + m : null;
              })();

              customTimes
                .filter(ct => (ct.dayIndex ?? 0) === dayIdx && ct.label)
                .forEach(ct => {
                  const time = ct.type === 'relative'
                    ? resolveMoadRelative(ct.baseTime, ct.offset, ct.roundTo || 0)
                    : ct.time;
                  if (!time || time === '--:--') return;

                  // קביעת יום התפילה:
                  // 1. אם ה-baseTime מוגדר כמפתח-ערב (dayEntrySunset / dayEntryTime) → תמיד ערב החג
                  // 2. אחרת: תפילה שעתה >= entryTime נחשבת ערב-חג (מתרחשת בלילה שלפני הת"ל)
                  // 3. תפילת יום (שעה < entryTime) → ביום המועד עצמו
                  const prayerMins = (() => {
                    const [h, m] = time.split(':').map(Number);
                    return !isNaN(h) && !isNaN(m) ? h * 60 + m : null;
                  })();
                  // moadPart רלוונטי רק לפריטים קבועים (fixed) — לא יחסיים
                  // יחסי: קביעה לפי EVE_SOURCE_KEYS בלבד (baseTime קובע)
                  // קבוע: moadPart מפורש, fallback לheuristic 60 דק' לפני כניסה
                  const isEveningPrayer = ct.type === 'relative'
                    ? EVE_SOURCE_KEYS.has(ct.baseTime)
                    : ct.moadPart === 'eve'
                    ? true
                    : ct.moadPart === 'day'
                    ? false
                    : daysOffset > 0 && prayerMins !== null && entryMins !== null && prayerMins >= entryMins - 60;
                  const actualHoursAhead = isEveningPrayer ? (daysOffset - 1) * 24 : daysOffset * 24;

                  // actualHoursAhead שלילי = תפילה שכבר עברה (ערב אתמול) — דלג
                  if (actualHoursAhead < 0) return;

                  // legacy (ללא moadPart): פריט קבוע שהיה מסווג כתפילת-ערב אמש → כבר הוצג, דלג
                  if (!ct.moadPart && daysOffset === 0 && ct.type !== 'relative' &&
                      prayerMins !== null && entryMins !== null && prayerMins >= entryMins - 60) return;

                  result.push({ label: ct.label, time, type: 'highlighted', hoursAhead: actualHoursAhead, accentColor: srcMoadColor, isMoad: true });
                });

              // כניסת המועד: entryTime = צאת כוכבים של הלילה שלפני — כמו תפילת ערב
              const entryHoursAhead = (daysOffset - 1) * 24;
              if (entryHoursAhead >= 0 && moadDay.entryTime && moadDay.entryTime !== '--:--') {
                result.push({ label: 'כניסת המועד', time: moadDay.entryTime, type: 'simple', hoursAhead: entryHoursAhead, accentColor: srcMoadColor, isMoad: true });
              }

              // יציאת המועד: exitTime = צאת כוכבים של יום המועד עצמו
              const exitHoursAhead = daysOffset * 24;
              if (moadDay.exitTime && moadDay.exitTime !== '--:--') {
                result.push({ label: 'יציאת המועד', time: moadDay.exitTime, type: 'simple', hoursAhead: exitHoursAhead, accentColor: srcMoadColor, isMoad: true });
              }
            });
          });
          return result;
        })();

        // האם היום הוא יום מועד (כמו isShabbat לשבת)
        const isMoadToday = widgets.filter(w => w.componentKey === 'MoadWidget').some(mw => {
          const days = moadByWidgetId[mw.widgetId]?.data?.days || [];
          return days.some(d => {
            if (d.date !== _moadLookaheadDates[0]) return false;
            if (d.exitTime && d.exitTime !== '--:--') {
              const [eh, em] = d.exitTime.split(':').map(Number);
              if (!isNaN(eh) && !isNaN(em) && _nowMinsForMoad > eh * 60 + em) return false;
            } else if (isPastNightfallToday) {
              return false;
            }
            return true;
          });
        });

        // האם הלילה נכנס מועד (כמו isFriday לשבת — הצג חול עד חצות)
        const isErevMoadTonight = !isMoadToday && widgets.filter(w => w.componentKey === 'MoadWidget').some(mw => {
          const days = moadByWidgetId[mw.widgetId]?.data?.days || [];
          return days.some(d => d.date === _moadLookaheadDates[1]);
        });

        return (
          <WidgetWrapper
            type="zmanim"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
            style={{ fontSize: `${BASE_FONT_SIZE}px` }}
          >
            <ZmanimWidget
              zmanim={zmanim}
              shabbat={zmanimShabbat}
              hebrewDate={hebrewDate}
              loading={zmanimLoading}
              error={zmanimError}
              template={templateName}
              customTimes={(() => {
                const widgetOwnTimes = widget.props?.customTimes || [];

                // dedup מאוחד: מועד > תפילות > עצמי — מונע כפילויות בין מקורות
                const seen = new Set();
                const dedup = (item) => {
                  const key = `${item.time}|${item.hoursAhead ?? 0}`;
                  if (seen.has(key)) return false;
                  seen.add(key);
                  return true;
                };

                if (isMoadToday) {
                  // מועד — הסתר זמני חול, הצג רק זמני מועד (כמו שבת מסתיר חול)
                  return moadResolvedForZmanim.filter(dedup);
                }

                const moadDeduped    = moadResolvedForZmanim.filter(dedup);
                const tefilotDeduped = tefilotResolvedForZmanim.filter(dedup);
                const ownDeduped     = widgetOwnTimes.filter(dedup);

                if (isErevMoadTonight && zmanim?.chatzot) {
                  // ערב מועד — הצג זמני חול עד חצות בלבד (כמו ערב שבת)
                  const [ch, cm] = zmanim.chatzot.split(':').map(Number);
                  const beforeChatzot = (ct) => {
                    const [ph, pm] = (ct.time || '').split(':').map(Number);
                    if (isNaN(ph)) return true;
                    if (ph >= 0 && ph < 6) return false; // שעות קטנות = יום הבא
                    return ph < ch || (ph === ch && pm < cm);
                  };
                  return [
                    ...moadDeduped,
                    ...tefilotDeduped.filter(beforeChatzot),
                    ...ownDeduped.filter(beforeChatzot),
                  ];
                }

                return [...moadDeduped, ...tefilotDeduped, ...ownDeduped];
              })()}
              shabbatCustomTimes={shabbatCustomTimes}
              title={widget.props?.title || 'סדר היום'}
              showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : true}
              visibleZmanim={widget.props?.visibleZmanim || null}
              tefilotColor={srcTefilotColor}
              moadColor={srcMoadColor}
              shabbatColor={srcShabbatColor}
              shabbatEntryZman={shabbatWidget?.props?.entryZman || null}
              shabbatExitZman={shabbatWidget?.props?.exitZman || null}
              showInyaneiHayom={widget.props?.showInyaneiHayom || false}
              inyaneiDisplayMode={widget.props?.inyaneiDisplayMode || 'section'}
              inyaneiEdah={widget.props?.inyaneiEdah || 'ashkenaz'}
              inyaneiCategories={widget.props?.inyaneiCategories || null}
              location={organization?.location}
              dispatch={dispatch}
            />
          </WidgetWrapper>
        );
      }

      case 'WeatherWidget':
        return (
          <WidgetWrapper
            type="weather"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
          >
            <AutoScaler>
              <WeatherWidget
                template={templateName}
                location={organization?.location}
                cityName={organization?.address?.city}
                weather={weather}
                loading={weatherLoading}
                error={weatherError}
                title={widget.props?.title || 'מזג אוויר'}
                showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : false}
                dispatch={dispatch}
              />
            </AutoScaler>
          </WidgetWrapper>
        );

      case 'TextWidget':
        return (
          <WidgetWrapper
            type="fixedText"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
            style={{ fontSize: `${BASE_FONT_SIZE}px` }}
          >
            <TextWidget
              text={widget.props?.text || ''}
              template={templateName}
              onSave={(data) => {
                const updatedWidgets = widgets.map(w =>
                  w.widgetId === widget.widgetId
                    ? { ...w, props: { text: data.text } }
                    : w
                );
                dispatch(updateDashboardLocal(updatedWidgets, screenId));
              }}
            />
          </WidgetWrapper>
        );

      case 'ImageWidget':
        return (
          <WidgetWrapper
            type="image"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
            style={{ fontSize: `${BASE_FONT_SIZE}px` }}
          >
            <ImageWidget
              imageUrl={widget.props?.imageUrl || ''}
              template={templateName}
              onSave={(url) => {
                const updatedWidgets = widgets.map(w =>
                  w.widgetId === widget.widgetId
                    ? { ...w, props: { ...w.props, imageUrl: url } }
                    : w
                );
                dispatch(updateDashboardLocal(updatedWidgets, screenId));
              }}
            />
          </WidgetWrapper>
        );

      case 'DocumentWidget':
        return (
          <WidgetWrapper
            type="document"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
          >
            <DocumentWidget
              documentUrl={widget.props?.documentUrl || ''}
              documentType={widget.props?.documentType || ''}
              pageNumber={widget.props?.pageNumber || 1}
              autoScroll={widget.props?.autoScroll || false}
              scrollInterval={widget.props?.scrollInterval || 10}
              fileName={widget.props?.fileName || ''}
              template={templateName}
            />
          </WidgetWrapper>
        );

      case 'ShabbatWidget':
        return (
          <WidgetWrapper
            type="shabbat"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
          >
            <AutoScaler>
              <ShabbatWidget
                shabbat={shabbat}
                loading={shabbatLoading}
                error={shabbatError}
                template={templateName}
                customTimes={widget.props?.customTimes || []}
                drashot={widget.props?.drashot || []}
                title={widget.props?.title || 'זמני שבת'}
                showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : true}
                entryIcon={widget.props?.entryIcon || 'ShabbatCandles'}
                exitIcon={widget.props?.exitIcon || 'MoonStar'}
                entryZman={widget.props?.entryZman || null}
                exitZman={widget.props?.exitZman   || null}
                bgColor={widget.widgetSettings?.bgColor}
                showTimeRule={widget.props?.showTimeRule || false}
                showEntryExit={widget.props?.showEntryExit ?? true}
                showLeyning={widget.props?.showLeyning || false}
                showHaftarah={widget.props?.showHaftarah || false}
                leyningDisplayMode={widget.props?.leyningDisplayMode || 'summary'}
                location={organization?.location}
                dispatch={dispatch}
              />
            </AutoScaler>
          </WidgetWrapper>
        );

      case 'MoadWidget': {
        const moadStateData = moadByWidgetId[widget.widgetId];
        return (
          <WidgetWrapper
            type="moad"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
          >
            <AutoScaler>
              <MoadWidget
                moadData={moadStateData?.data}
                loading={moadStateData?.loading}
                error={moadStateData?.error}
                customTimes={widget.props?.customTimes || []}
                days={widget.props?.days || []}
                template={templateName}
                title={widget.props?.title || 'מועד'}
                showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : true}
                entryIcon={widget.props?.entryIcon || 'ShabbatCandles'}
                exitIcon={widget.props?.exitIcon || 'MoonStar'}
                globalEntryZman={widget.props?.globalEntryZman || null}
                globalExitZman={widget.props?.globalExitZman || null}
                drashot={widget.props?.drashot || []}
                showLeyning={widget.props?.showLeyning || false}
                showHaftarah={widget.props?.showHaftarah || false}
                leyningDisplayMode={widget.props?.leyningDisplayMode || 'summary'}
                widgetId={widget.widgetId}
                location={organization?.location}
                dispatch={dispatch}
              />
            </AutoScaler>
          </WidgetWrapper>
        );
      }

      case 'TefilotWidget': {
        const tefilotStateData = tefilotByWidgetId[widget.widgetId];
        return (
          <WidgetWrapper
            type="tefilot"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
          >
            <AutoScaler>
              <TefilotWidget
                tefilotData={tefilotStateData?.data}
                loading={tefilotStateData?.loading}
                error={tefilotStateData?.error}
                customTimes={widget.props?.customTimes || []}
                recurringDisplay={widget.props?.recurringDisplay || 'compact'}
                showRuleMode={widget.props?.showRuleMode || false}
                highlightNextMinyan={widget.props?.highlightNextMinyan || false}
                template={templateName}
                title={widget.props?.title || 'תפילות יום חול'}
                showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : true}
                widgetId={widget.widgetId}
                location={organization?.location}
                dispatch={dispatch}
              />
            </AutoScaler>
          </WidgetWrapper>
        );
      }

      case 'NoticeBoardWidget':
        return (
          <WidgetWrapper
            type="noticeBoard"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
            style={{ fontSize: `${BASE_FONT_SIZE}px` }}
          >
            <NoticeBoardWidget
              data={widget.props?.data || { notices: [] }}
              template={templateName}
              title={widget.props?.title || 'לוח מודעות'}
              showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : true}
            />
          </WidgetWrapper>
        );

      case 'EmergencyWidget':
        return (
          <WidgetWrapper
            type="emergency"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
          >
            <AutoScaler>
              <EmergencyWidget
                initialData={widget.props?.data || { volunteers: { medic: [], paramedic: [], maar: [] } }}
                template={templateName}
                title={widget.props?.title || 'חירום והצלה'}
                showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : true}
                columns={widget.props?.columns}
                onSave={(data) => {
                  const updatedWidgets = widgets.map(w =>
                    w.widgetId === widget.widgetId
                      ? { ...w, props: { ...w.props, data } }
                      : w
                  );
                  dispatch(updateDashboardLocal(updatedWidgets, screenId));
                }}
              />
            </AutoScaler>
          </WidgetWrapper>
        );

      case 'ContactsWidget':
        return (
          <WidgetWrapper
            type="contacts"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
          >
            <AutoScaler>
              <ContactsWidget
                initialData={widget.props?.data || { contacts: [] }}
                template={templateName}
                title={widget.props?.title || 'אנשי קשר והנהלה'}
                showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : true}
                columns={widget.props?.columns}
                onSave={(data) => {
                  const updatedWidgets = widgets.map(w =>
                    w.widgetId === widget.widgetId
                      ? { ...w, props: { ...w.props, data } }
                      : w
                  );
                  dispatch(updateDashboardLocal(updatedWidgets, screenId));
                }}
              />
            </AutoScaler>
          </WidgetWrapper>
        );

      case 'SimchaWidget':
        return (
          <WidgetWrapper
            type="simcha"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
            style={{ fontSize: `${BASE_FONT_SIZE}px` }}
          >
            <SimchaWidget
              initialData={widget.props?.data || { simchas: [] }}
              template={templateName}
              title={widget.props?.title || 'שמחות בקהילה'}
              showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : true}
              bgColor={widget.widgetSettings?.bgColor}
              onSave={(data) => {
                const updatedWidgets = widgets.map(w =>
                  w.widgetId === widget.widgetId
                    ? { ...w, props: { ...w.props, data } }
                    : w
                );
                dispatch(updateDashboardLocal(updatedWidgets, screenId));
              }}
            />
          </WidgetWrapper>
        );

      case 'NitzavSpotlightWidget':
        return (
          <WidgetWrapper
            type="nitzavSpotlight"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
          >
            <AutoScaler>
              <NitzavSpotlightWidget
                data={widget.props?.data || { name: 'תורם הקהילה', dedication: 'לעילוי נשמת', type: 'נציב היום', iconId: 'star' }}
                title={widget.props?.title || 'נציב הקהילה'}
                showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : false}
                onSave={(data) => {
                  const updatedWidgets = widgets.map(w =>
                    w.widgetId === widget.widgetId
                      ? { ...w, props: { ...w.props, data } }
                      : w
                  );
                  dispatch(updateDashboardLocal(updatedWidgets, screenId));
                }}
              />
            </AutoScaler>
          </WidgetWrapper>
        );

      case 'ParnasWidget': {
        const parnasState = parnassByWidgetId[widget.widgetId];
        return (
          <WidgetWrapper
            type="parnas"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
          >
            {/* WeeklyYear: position:absolute כדי ש-height:100% יפתר נכון ללא AutoScaler */}
            {/* שאר התבניות: AutoScaler מטפל בסקייל פרופורציונלי של הפונטים */}
            {templateName === 'WeeklyYear' ? (
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <div style={{ position: 'absolute', inset: 0 }}>
                  <ParnasWidget
                    widgetId={widget.widgetId}
                    parnasData={parnasState?.data}
                    loading={parnasState?.loading}
                    error={parnasState?.error}
                    dispatch={dispatch}
                    location={organization?.location}
                    sponsors={widget.props?.sponsors || []}
                    parnasType={widget.props?.parnasType || 'maor'}
                    parnasTypeCustomLabel={widget.props?.parnasTypeCustomLabel || ''}
                    parnasTypeIcon={widget.props?.parnasTypeIcon || 'star'}
                    template={templateName}
                    title={widget.props?.title || 'פרנס הקהילה'}
                    showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : false}
                    tileColors={widget.props?.tileColors}
                    placeholderName={widget.props?.placeholderName}
                    placeholderNote={widget.props?.placeholderNote}
                    weeklyYearDisplayYear={widget.props?.weeklyYearDisplayYear}
                    weeksAheadCount={widget.props?.weeksAheadCount}
                    honorCardPriority={widget.props?.honorCardPriority}
                    includedSlotTypes={widget.props?.includedSlotTypes}
                  />
                </div>
              </div>
            ) : (
              <AutoScaler>
                <ParnasWidget
                  widgetId={widget.widgetId}
                  parnasData={parnasState?.data}
                  loading={parnasState?.loading}
                  error={parnasState?.error}
                  dispatch={dispatch}
                  location={organization?.location}
                  sponsors={widget.props?.sponsors || []}
                  parnasType={widget.props?.parnasType || 'maor'}
                  parnasTypeCustomLabel={widget.props?.parnasTypeCustomLabel || ''}
                  parnasTypeIcon={widget.props?.parnasTypeIcon || 'star'}
                  template={templateName}
                  title={widget.props?.title || 'פרנס הקהילה'}
                  showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : false}
                  tileColors={widget.props?.tileColors}
                  placeholderName={widget.props?.placeholderName}
                  placeholderNote={widget.props?.placeholderNote}
                  weeklyYearDisplayYear={widget.props?.weeklyYearDisplayYear}
                  weeksAheadCount={widget.props?.weeksAheadCount}
                  honorCardPriority={widget.props?.honorCardPriority}
                  includedSlotTypes={widget.props?.includedSlotTypes}
                />
              </AutoScaler>
            )}
          </WidgetWrapper>
        );
      }

      case 'OmerWidget': {
        const omerState = omerByWidgetId[widget.widgetId];
        return (
          <WidgetWrapper
            type="omer"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
          >
            <AutoScaler>
              <OmerWidget
                widgetId={widget.widgetId}
                omerData={omerState?.data}
                loading={omerState?.loading}
                error={omerState?.error}
                dispatch={dispatch}
                location={organization?.location}
                nusach={widget.props?.nusach || 'ashkenaz'}
                showBracha={widget.props?.showBracha ?? true}
                showFullNusach={widget.props?.showFullNusach ?? true}
                showLeshemYichud={widget.props?.showLeshemYichud ?? false}
                showPasuk={widget.props?.showPasuk || 'none'}
                showAfterCount={widget.props?.showAfterCount ?? false}
                showPreview={widget.props?.showPreview ?? false}
                template={templateName}
                title={widget.props?.title || 'ספירת העומר'}
                showTitle={widget.props?.showTitle ?? false}
                bgColor={widget.widgetSettings?.bgColor}
              />
            </AutoScaler>
          </WidgetWrapper>
        );
      }

      case 'HalachicTimesWidget': {
        const horizontalTemplates = ['Banner', 'Ribbon'];
        const isHorizontal = horizontalTemplates.includes(templateName);
        const halachicTimesContent = (
          <HalachicTimesWidget
            zmanim={zmanim}
            hebrewDate={hebrewDate}
            visibleZmanim={widget.props?.visibleZmanim || null}
            template={templateName}
            title={widget.props?.title || '\u05D6\u05DE\u05E0\u05D9 \u05D4\u05D9\u05D5\u05DD \u05D4\u05D4\u05DC\u05DB\u05EA\u05D9\u05D9\u05DD'}
            showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : true}
            showCountdown={widget.props?.showCountdown ?? true}
            location={organization?.location}
            dispatch={dispatch}
          />
        );
        return (
          <WidgetWrapper
            type="halachicTimes"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
          >
            {isHorizontal ? halachicTimesContent : <AutoScaler>{halachicTimesContent}</AutoScaler>}
          </WidgetWrapper>
        );
      }

      case 'InyaneiHaYomWidget':
        return (
          <WidgetWrapper
            type="inyaneiHayom"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
          >
            <AutoScaler>
              <InyaneiHaYomWidget
                template={templateName}
                title={widget.props?.title || 'ענייני היום'}
                showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : true}
                edah={widget.props?.edah || 'ashkenaz'}
                showTachanun={widget.props?.showTachanun ?? true}
                showHallel={widget.props?.showHallel ?? true}
                showPrayerAdditions={widget.props?.showPrayerAdditions ?? true}
                showSeasonalPrayers={widget.props?.showSeasonalPrayers ?? true}
                showSeasonalTransitions={widget.props?.showSeasonalTransitions ?? true}
                showSelichot={widget.props?.showSelichot ?? true}
                showWeekdayPrayers={widget.props?.showWeekdayPrayers ?? true}
                showShabbatAdditions={widget.props?.showShabbatAdditions ?? true}
                showMotzaeiShabbat={widget.props?.showMotzaeiShabbat ?? true}
                showSeasonalCustoms={widget.props?.showSeasonalCustoms ?? true}
                showLiturgy={widget.props?.showLiturgy ?? true}
                showLeyning={widget.props?.showLeyning || false}
                showHaftarah={widget.props?.showHaftarah || false}
                showMourningPeriods={widget.props?.showMourningPeriods ?? true}
                showLimudim={widget.props?.showLimudim ?? false}
                lookaheadDays={widget.props?.lookaheadDays ?? 0}
                location={organization?.location}
              />
            </AutoScaler>
          </WidgetWrapper>
        );

      case 'LimudimWidget':
        return (
          <WidgetWrapper
            type="limudim"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
          >
            <AutoScaler>
              <LimudimWidget
                template={templateName}
                title={widget.props?.title || 'לימודים יומיים'}
                showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : true}
                showDafYomi={widget.props?.showDafYomi ?? true}
                showMishnaYomi={widget.props?.showMishnaYomi ?? true}
                showNachYomi={widget.props?.showNachYomi ?? false}
                showRambam1={widget.props?.showRambam1 ?? true}
                showRambam3={widget.props?.showRambam3 ?? false}
                showYerushalmiVilna={widget.props?.showYerushalmiVilna ?? false}
                showYerushalmiSchottenstein={widget.props?.showYerushalmiSchottenstein ?? false}
                showChofetzChaim={widget.props?.showChofetzChaim ?? false}
                showShemiratHaLashon={widget.props?.showShemiratHaLashon ?? false}
                showSeferHaMitzvot={widget.props?.showSeferHaMitzvot ?? false}
                showKitzurShulchanAruch={widget.props?.showKitzurShulchanAruch ?? false}
                showArukhHaShulchanYomi={widget.props?.showArukhHaShulchanYomi ?? false}
                showPsalms={widget.props?.showPsalms ?? false}
                showTanakhYomi={widget.props?.showTanakhYomi ?? false}
                showPirkeiAvotSummer={widget.props?.showPirkeiAvotSummer ?? false}
                showPerekYomi={widget.props?.showPerekYomi ?? false}
                showDafWeekly={widget.props?.showDafWeekly ?? false}
                showDirshuAmudYomi={widget.props?.showDirshuAmudYomi ?? false}
                location={organization?.location}
              />
            </AutoScaler>
          </WidgetWrapper>
        );

      case 'ExpensesAnalyticsWidget':
        return (
          <WidgetWrapper
            type="expensesAnalytics"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
          >
            <AutoScaler>
              <ExpensesAnalyticsWidget
                data={widget.props?.data || { categories: [{ name: 'תחזוקה', amount: 5000, color: '#4ECDC4' }] }}
                freeText={widget.props?.freeText || ''}
                template={templateName}
                title={widget.props?.title || 'פילוח הוצאות'}
                showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : true}
                onSave={(data) => {
                  const updatedWidgets = widgets.map(w =>
                    w.widgetId === widget.widgetId
                      ? { ...w, props: { ...w.props, data } }
                      : w
                  );
                  dispatch(updateDashboardLocal(updatedWidgets, screenId));
                }}
              />
            </AutoScaler>
          </WidgetWrapper>
        );

      case 'FinancialGaugeWidget':
        return (
          <WidgetWrapper
            type="financialGauge"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
          >
            <AutoScaler>
              <FinancialGaugeWidget
                data={widget.props?.data || {
                  incomeData: [{ id: 'donations', label: 'תרומות', value: 50000, color: '#C5A572' }],
                  expensesData: [{ id: 'salaries', label: 'משכורות', value: 40000, color: '#1e3a5f' }]
                }}
                template={templateName}
                title={widget.props?.title || 'תמונת מצב כספית'}
                showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : true}
                onSave={(data) => {
                  const updatedWidgets = widgets.map(w =>
                    w.widgetId === widget.widgetId
                      ? { ...w, props: { ...w.props, data } }
                      : w
                  );
                  dispatch(updateDashboardLocal(updatedWidgets, screenId));
                }}
              />
            </AutoScaler>
          </WidgetWrapper>
        );

      case 'ClockWidget':
        return (
          <WidgetWrapper
            type="clock"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'none'}
          >
            <AutoScaler>
              <ClockWidget
                template={templateName}
                textColor={widget.props?.textColor}
                fontFamily={FONT_MAP[dashboardFontFamily] || undefined}
                accentColor={widget.props?.accentColor}
              />
            </AutoScaler>
          </WidgetWrapper>
        );

      case 'SeatingMapWidget':
        return (
          <WidgetWrapper
            type="seatingMap"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
          >
            <AutoScaler>
              <SeatingMapWidget
                mapData={widget.props?.mapData}
                template={templateName}
                title={widget.props?.title || 'מפת מושבים'}
                showTitle={widget.props?.showTitle ?? true}
              />
            </AutoScaler>
          </WidgetWrapper>
        );

      case 'NextMinyaWidget':
        return (
          <WidgetWrapper
            type="nextMinya"
            bgColor={widget.widgetSettings?.bgColor}
            borderType={widget.widgetSettings?.borderType || 'default'}
          >
            <AutoScaler>
              <NextMinyaWidget
                widgetId={widget.widgetId}
                template={templateName}
                title={widget.props?.title || 'מניין הבא'}
                showTitle={widget.props?.showTitle !== undefined ? widget.props.showTitle : true}
                location={organization?.location}
                tefilotColor={widget.props?.tefilotColor || '#0369a1'}
                moadColor={widget.props?.moadColor || '#b45309'}
                shabbatColor={widget.props?.shabbatColor || '#7c3aed'}
                bgColor={widget.widgetSettings?.bgColor || ''}
                allWidgetsProp={widgets}
              />
            </AutoScaler>
          </WidgetWrapper>
        );

      default:
        return (
          <div style={{ padding: '20px', textAlign: 'center', color: '#ff0000' }}>
            <h3>❌ רכיב לא ידוע</h3>
            <p>{widget.componentKey}</p>
          </div>
        );
    }
  };

  // Convert widgets to grid layout format
  const gridLayout = widgets.map(widget => ({
    i: widget.widgetId,
    x: widget.dashboardLayout.x,
    y: widget.dashboardLayout.y,
    w: widget.dashboardLayout.w,
    h: widget.dashboardLayout.h
  }));

  return (
    <div
      ref={containerRef}
      style={{
        ...styles.container,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        ...style
      }}>

      <div style={{
        ...styles.gridContainer,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: '#000000',
      }}>

        {/* Wrapper חדש שמגדיר את הגודל המדויק */}
        <div style={{
          width: `${TARGET_WIDTH * scale}px`,
          height: `${TARGET_HEIGHT * scale}px`,
          position: 'relative',
          ...(isEditModeProp === 'editor' && { outline: '6px solid #111', borderRadius: '6px' }),
        }}>

          {/* Canvas: משטח העבודה - ללא transform, אלא עם מידות מחושבות */}
          <ThemeProvider theme={widgetTheme}>
          <div style={{
            width: TARGET_WIDTH * scale,
            height: TARGET_HEIGHT * scale,
            background: dashboardBgImage ? `url(${dashboardBgImage}) center/cover no-repeat` : dashboardBgColor,
            position: 'relative',
            '--dashboard-border-radius': `${dashboardBorderRadius}px`,
            '--dashboard-widget-shadow': (() => {
              if (dashboardWidgetShadow === 'none') return 'none';
              const hex = dashboardWidgetShadowColor || '#000000';
              const r = parseInt(hex.slice(1, 3), 16);
              const g = parseInt(hex.slice(3, 5), 16);
              const b = parseInt(hex.slice(5, 7), 16);
              const alphas = { subtle: 0.1, medium: 0.18, strong: 0.3 };
              const a = alphas[dashboardWidgetShadow] || 0.1;
              const offsets = { subtle: '0 2px 8px', medium: '0 4px 20px', strong: '0 8px 36px' };
              return `${offsets[dashboardWidgetShadow]} rgba(${r},${g},${b},${a})`;
            })(),
            fontFamily: FONT_MAP[dashboardFontFamily] || 'inherit',
          }}>

            <GridLayout
              className="layout"
              layout={gridLayout}

              // Grid dimensions
              cols={COLS}
              rowHeight={ROW_HEIGHT * scale}
              width={TARGET_WIDTH * scale}
              maxRows={maxRows}
              margin={[MARGIN * scale, MARGIN * scale]}
              containerPadding={[MARGIN * scale, MARGIN * scale]}

              // Scale support - חשוב מאוד!
              transformScale={1}

              // Drag & Drop settings
              isDraggable={!isViewingVersion}
              isResizable={!isViewingVersion}
              draggableHandle=".widget-drag-handle"

              // Resize handles
              resizeHandles={isViewingVersion ? [] : ['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne']}

              // Layout behavior - מניעת חפיפות וללא הזזות אוטומטיות
              preventCollision={true}
              compactType={null}

              // Callbacks
              onLayoutChange={handleLayoutChange}
              onDragStop={handleDragStop}
              onResizeStop={handleResizeStop}
            >


              {widgets.map((widget) => {
                const borderType = widget.widgetSettings?.borderType || 'default';
                const widgetBorder = borderType === 'none' ? 'none'
                  : borderType === 'colored' ? `1px solid #8a6d08`
                  : `1px solid #e8e6e1`;
                const suppressShadow = widget.widgetSettings?.bgColor === 'transparent' && borderType === 'none';
                return (
                <div
                  key={widget.widgetId}
                  className="widget-drag-handle"
                  style={{ ...styles.item, border: widgetBorder, ...(suppressShadow && { boxShadow: 'none' }) }}
                  onMouseEnter={() => setHoveredWidget(widget.widgetId)}
                  onMouseLeave={() => setHoveredWidget(null)}
                >
                  {/* כפתורי עריכה ומחיקה - מופיעים רק ב-hover */}
                  {!isViewingVersion && (
                    <div
                      style={{
                        ...styles.itemHeader,
                        opacity: hoveredWidget === widget.widgetId ? 1 : 0,
                        pointerEvents: hoveredWidget === widget.widgetId ? 'auto' : 'none'
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <Box
                        onClick={(e) => { e.stopPropagation(); handleEditWidget(widget); }}
                        onMouseDown={(e) => e.stopPropagation()}
                        sx={{
                          bgcolor: '#1e3a5f',
                          borderRadius: '7px',
                          px: 1.3, py: 0.6,
                          display: 'flex', alignItems: 'center', gap: 0.6,
                          cursor: 'pointer',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.22)',
                          transition: 'background-color 0.15s, transform 0.12s',
                          '&:hover': { bgcolor: '#2d5a8f', transform: 'scale(1.06)' },
                          '&:active': { transform: 'scale(0.94)', bgcolor: '#0f2040' },
                        }}
                      >
                        <CreateIcon sx={{ fontSize: 14, color: 'white' }} />
                        <Typography sx={{ fontSize: '0.78rem', color: 'white', fontWeight: 700, lineHeight: 1 }}>עריכה</Typography>
                      </Box>

                      {/* כפתור מחיקה */}
                      <Box
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(widget.widgetId); }}
                        onMouseDown={(e) => e.stopPropagation()}
                        sx={{
                          bgcolor: '#c0392b',
                          borderRadius: '7px',
                          px: 1.3, py: 0.6,
                          display: 'flex', alignItems: 'center', gap: 0.6,
                          cursor: 'pointer',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.22)',
                          transition: 'background-color 0.15s, transform 0.12s',
                          '&:hover': { bgcolor: '#e74c3c', transform: 'scale(1.06)' },
                          '&:active': { transform: 'scale(0.94)', bgcolor: '#8e1c1c' },
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 14, color: 'white' }} />
                        <Typography sx={{ fontSize: '0.78rem', color: 'white', fontWeight: 700, lineHeight: 1 }}>מחיקה</Typography>
                      </Box>
                    </div>
                  )}

                  <div style={styles.itemContent}>
                    {/* טריק הסקיילינג ההפוך: מגדילים את הקונטיינר הפנימי ומקטינים ויזואלית */}
                    <div style={{
                      width: `calc(${100 / scale}% - 1px)`,
                      height: `calc(${100 / scale}% - 1px)`,
                      transform: `scale(${scale})`,
                      transformOrigin: '0 0',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      {renderWidget(widget)}
                    </div>
                  </div>
                </div>
              );
              })}

            </GridLayout>
          </div>
          </ThemeProvider>
        </div>
      </div>

      {/* ── Unified Widget Configurator ── */}
      <WidgetConfigurator
        open={configuratorOpen}
        componentKey={configuratorKey}
        widgetConfig={currentWidgetConfig}
        isEditMode={isEditMode}
        initialData={configuratorInitialData}
        onSave={handleConfiguratorSave}
        onClose={() => {
          setConfiguratorOpen(false);
          setConfiguratorKey(null);
          setConfiguratorInitialData(null);
          setCurrentWidgetConfig(null);
          if (isEditMode) {
            setEditingWidget(null);
            setIsEditMode(false);
          }
        }}
        onUploadImage={async (file) => {
          if (isDemo) return null;
          const isDocument = file.type === 'application/pdf' ||
            file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
            file.type === 'application/msword';
          if (isDocument) {
            const { StorageService } = await import('../../../sdk/storageService');
            return StorageService.uploadDocument(file);
          }
          return dispatch(uploadImage(file));
        }}
        onFetchZmanimForDay={handleFetchZmanimForDay}
        onFetchTodayZmanim={configuratorKey === 'ShabbatWidget' ? handleFetchFridayZmanim : handleFetchTodayZmanim}
      />
      {/* Settings Dialog */}
      <ConfigDialog
        open={showSettingsDialog}
        onClose={() => {
          setTempBgColor(dashboardBgColor);
          setTempBgImage(dashboardBgImage);
          setTempBorderRadius(dashboardBorderRadius);
          setTempFontFamily(dashboardFontFamily);
          setTempWidgetShadow(dashboardWidgetShadow);
          setTempWidgetShadowColor(dashboardWidgetShadowColor);
          setShowSettingsDialog(false);
        }}
        onConfirm={() => {
          setDashboardBgColor(tempBgColor);
          setDashboardBgImage(tempBgImage);
          setDashboardBorderRadius(tempBorderRadius);
          setDashboardFontFamily(tempFontFamily);
          setDashboardWidgetShadow(tempWidgetShadow);
          setDashboardWidgetShadowColor(tempWidgetShadowColor);
          const settings = {
            bgColor: tempBgColor,
            bgImage: tempBgImage,
            borderRadius: tempBorderRadius,
            fontFamily: tempFontFamily,
            widgetShadow: tempWidgetShadow,
            widgetShadowColor: tempWidgetShadowColor,
          };
          dispatch(updateDashboardSettings(settings));
          setShowSettingsDialog(false);
        }}
        confirmText="אישור"
        title="הגדרות לוח"
        width={560}
        tabs={[
          {
            label: 'רקע',
            content: (
              <Stack spacing={3}>
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#475569', mb: 1.5, fontSize: '0.875rem' }}>רקע לוח</Typography>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, bgcolor: 'white', border: '1px solid #e2e8f0' }}>
                    <Stack spacing={2.5}>
                      <Box>
                        <Typography sx={{ fontSize: '0.8rem', color: '#64748b', mb: 1.5 }}>בחר צבע</Typography>
                        <Box
                          component="input"
                          type="color"
                          value={tempBgColor}
                          onChange={(e) => {
                            setTempBgColor(e.target.value);
                            setTempBgImage('');
                          }}
                          sx={{ width: '100%', height: 60, borderRadius: 2, border: '2px solid #e2e8f0', cursor: 'pointer', '&:hover': { borderColor: '#3b82f6' } }}
                        />
                        <Button
                          variant="text"
                          onClick={() => {
                            setTempBgColor('#fcfbfa');
                            setTempBgImage('');
                          }}
                          sx={{ mt: 1, width: '100%', color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}
                        >
                          ללא רקע (ברירת מחדל)
                        </Button>
                      </Box>
                      <Divider sx={{ borderColor: '#f1f5f9' }} />
                      <Box>
                        <Typography sx={{ fontSize: '0.8rem', color: '#64748b', mb: 1.5 }}>או העלה תמונת רקע</Typography>
                        <Stack spacing={2} alignItems="center">
                          {tempBgImage && (
                            <Avatar
                              src={tempBgImage}
                              variant="square"
                              sx={{ width: '100%', height: 150, border: '2px solid #3b82f6', borderRadius: 2 }}
                            />
                          )}
                          <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                            <Button
                              component="label"
                              variant="contained"
                              startIcon={uploadingImage ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                              disabled={uploadingImage}
                              fullWidth
                              sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
                            >
                              העלה תמונה
                              <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  if (!file.type.startsWith('image/')) {
                                    alert('יש להעלות קובץ תמונה בלבד');
                                    return;
                                  }
                                  if (file.size > 5 * 1024 * 1024) {
                                    alert('גודל הקובץ חייב להיות עד 5MB');
                                    return;
                                  }
                                  if (isDemo) return;
                                  setUploadingImage(true);
                                  try {
                                    const url = await dispatch(uploadImage(file));
                                    setTempBgImage(url);
                                  } catch (err) {
                                    alert('שגיאה בהעלאת התמונה');
                                  } finally {
                                    setUploadingImage(false);
                                  }
                                }}
                              />
                            </Button>
                            {tempBgImage && !uploadingImage && (
                              <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={async () => {
                                  if (isDemo) { setTempBgImage(''); return; }
                                  try {
                                    await dispatch(deleteImage(tempBgImage));
                                    setTempBgImage('');
                                  } catch (err) {
                                    alert('שגיאה במחיקת התמונה');
                                  }
                                }}
                              >
                                מחק
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                      </Box>
                    </Stack>
                  </Paper>
                </Box>
              </Stack>
            )
          },
          {
            label: 'עיצוב',
            content: (
              <Stack spacing={3}>
                {/* Border Radius */}
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#475569', mb: 1.5, fontSize: '0.875rem' }}>עיגול פינות רכיבים</Typography>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, bgcolor: 'white', border: '1px solid #e2e8f0' }}>
                    <Stack spacing={2}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                          <Slider
                            value={tempBorderRadius}
                            onChange={(_, val) => setTempBorderRadius(val)}
                            min={0}
                            max={50}
                            step={1}
                            sx={{ color: '#1e3a5f' }}
                          />
                        </Box>
                        <Typography sx={{ fontSize: '0.875rem', color: '#374151', fontWeight: 600, minWidth: 40, textAlign: 'center' }}>
                          {tempBorderRadius}px
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1.5} justifyContent="center">
                        {[0, 8, 16, 24, 35, 50].map(val => (
                          <Box
                            key={val}
                            onClick={() => setTempBorderRadius(val)}
                            sx={{
                              width: 44,
                              height: 44,
                              border: tempBorderRadius === val ? '2px solid #1e3a5f' : '1px solid #e2e8f0',
                              borderRadius: `${val * 0.6}px`,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7rem',
                              color: tempBorderRadius === val ? '#1e3a5f' : '#9ca3af',
                              fontWeight: tempBorderRadius === val ? 700 : 400,
                              bgcolor: tempBorderRadius === val ? '#f0f4ff' : 'white',
                              transition: 'all 0.15s',
                              '&:hover': { borderColor: '#1e3a5f' }
                            }}
                          >
                            {val}
                          </Box>
                        ))}
                      </Stack>
                    </Stack>
                  </Paper>
                </Box>

                {/* Font Family */}
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#475569', mb: 1.5, fontSize: '0.875rem' }}>פונט</Typography>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, bgcolor: 'white', border: '1px solid #e2e8f0' }}>
                    <Stack spacing={1}>
                      {[
                        { value: 'default', label: 'ברירת מחדל', preview: 'שלום עולם' },
                        { value: 'assistant', label: 'Assistant', preview: 'שלום עולם' },
                        { value: 'rubik', label: 'Rubik', preview: 'שלום עולם' },
                        { value: 'heebo', label: 'Heebo', preview: 'שלום עולם' },
                        { value: 'frankRuhlLibre', label: 'Frank Ruhl Libre', preview: 'שלום עולם' },
                      ].map(font => (
                        <Box
                          key={font.value}
                          onClick={() => setTempFontFamily(font.value)}
                          sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            border: tempFontFamily === font.value ? '2px solid #1e3a5f' : '1px solid #e2e8f0',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            bgcolor: tempFontFamily === font.value ? '#f0f4ff' : 'white',
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: '#1e3a5f' }
                          }}
                        >
                          <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>{font.label}</Typography>
                          <Typography sx={{
                            fontFamily: {
                              default: 'inherit',
                              rubik: "'Rubik', sans-serif",
                              assistant: "'Assistant', sans-serif",
                              heebo: "'Heebo', sans-serif",
                              frankRuhlLibre: "'Frank Ruhl Libre', serif",
                            }[font.value],
                            fontSize: '1rem',
                            color: '#111827',
                            fontWeight: 500,
                          }}>
                            {font.preview}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Box>

                {/* Widget Shadow */}
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#475569', mb: 1.5, fontSize: '0.875rem' }}>צל רכיבים</Typography>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, bgcolor: 'white', border: '1px solid #e2e8f0' }}>
                    <Stack direction="row" spacing={1.5}>
                      {[
                        { value: 'none', label: 'ללא', shadow: 'none' },
                        { value: 'subtle', label: 'עדין', shadow: '0 2px 8px rgba(0,0,0,0.08)' },
                        { value: 'medium', label: 'בינוני', shadow: '0 4px 16px rgba(0,0,0,0.15)' },
                        { value: 'strong', label: 'חזק', shadow: '0 8px 32px rgba(0,0,0,0.25)' },
                      ].map(opt => (
                        <Box
                          key={opt.value}
                          onClick={() => setTempWidgetShadow(opt.value)}
                          sx={{
                            flex: 1,
                            p: 1.5,
                            borderRadius: 2,
                            border: tempWidgetShadow === opt.value ? '2px solid #1e3a5f' : '1px solid #e2e8f0',
                            cursor: 'pointer',
                            textAlign: 'center',
                            bgcolor: tempWidgetShadow === opt.value ? '#f0f4ff' : 'white',
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: '#1e3a5f' }
                          }}
                        >
                          <Box sx={{
                            width: '100%',
                            height: 36,
                            borderRadius: 1.5,
                            bgcolor: 'white',
                            border: '1px solid #e2e8f0',
                            boxShadow: opt.shadow,
                            mb: 1,
                          }} />
                          <Typography sx={{ fontSize: '0.75rem', color: tempWidgetShadow === opt.value ? '#1e3a5f' : '#9ca3af', fontWeight: tempWidgetShadow === opt.value ? 600 : 400 }}>
                            {opt.label}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Box>
              </Stack>
            )
          }
        ]}
      />

      {/* No Space Warning Dialog */}
      <CenterDialog
        open={noSpaceDialogOpen}
        onClose={() => setNoSpaceDialogOpen(false)}
        title="אין מספיק מקום"
        hideActions={true}
        minWidth="320px"
        maxWidth="450px"
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{
            color: '#475569',
            fontSize: '0.95rem',
            mb: 3
          }}>
            הלוח מלא — אין מספיק מקום להוספת הרכיב.
            <br />
            גרור רכיב קיים לפנות מקום, הקטן אותו, או מחק רכיב שאינך צריך.
          </Typography>
          <Button
            onClick={() => setNoSpaceDialogOpen(false)}
            variant="contained"
            sx={{
              bgcolor: '#1e3a5f',
              color: '#ffffff',
              '&:hover': {
                bgcolor: '#152844'
              },
              borderRadius: '8px',
              px: 4,
              py: 1,
              fontWeight: 600,
              minWidth: '120px'
            }}
          >
            הבנתי
          </Button>
        </Box>
      </CenterDialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            minWidth: '400px',
            maxWidth: '500px'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #e2e8f0',
          pb: 2,
          fontWeight: 600,
          color: '#1e3a5f'
        }}>
          מה ברצונך לעשות עם הרכיב?
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography sx={{
            color: '#475569',
            fontSize: '0.95rem',
            mb: 2
          }}>
            בחר אחת מהאופציות הבאות:
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          px: 3,
          pb: 3,
          gap: 1,
          flexDirection: 'column'
        }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSaveToArchive}
            sx={{
              bgcolor: '#2d5a8f',
              color: 'white',
              py: 1.5,
              fontSize: '0.95rem',
              fontWeight: 500,
              '&:hover': {
                bgcolor: '#1e3a5f'
              }
            }}
          >
            שמור ברכיבים שמורים
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleDeletePermanently}
            sx={{
              borderColor: '#dc2626',
              color: '#dc2626',
              py: 1.5,
              fontSize: '0.95rem',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#b91c1c',
                bgcolor: 'rgba(220, 38, 38, 0.04)'
              }
            }}
          >
            מחק לצמיתות
          </Button>
          <Button
            fullWidth
            variant="text"
            onClick={handleDeleteCancel}
            sx={{
              color: '#64748b',
              py: 1,
              fontSize: '0.9rem'
            }}
          >
            ביטול
          </Button>
        </DialogActions>
      </Dialog>

    </div >
  );
};

const styles = {
  container: { height: "100%", width: "100%", display: "flex", flexDirection: "column", overflow: "hidden", background: "#000000", margin: 0, padding: 0, boxSizing: "border-box" },
  gridContainer: { flex: 1, width: "100%", height: "100%", overflow: "auto", position: "relative", paddingBottom: 0, marginBottom: 0 },
  item: {
    background: "transparent",
    borderRadius: "var(--dashboard-border-radius, 35px)",
    display: "flex",
    flexDirection: "column",
    overflow: "visible",
    boxShadow: "var(--dashboard-widget-shadow, none)",
  },
  itemHeader: {
    position: "absolute",
    top: "8px",
    right: "8px",
    zIndex: 50,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "4px"
  },
  itemContent: { flex: 1, display: "flex", overflow: "hidden", position: "relative", width: "100%", height: "100%", borderRadius: "var(--dashboard-border-radius, 35px)" }
};

export default Dashboard;



